/**
 * Week Generator Service
 *
 * Handles automatic generation of weekly meal plans with variety constraints.
 * Ensures protein variety, cuisine variety, and respects cook time constraints.
 */

import { MealType } from '../redux/mealPlanSlice';
import { addDays, parseIsoDate } from '../utils/localDate';
import { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import { RecipeMetadata } from '../redux/recipeMetadataSlice';
import { LufaDeal } from '../redux/dealsSlice';
import { InventoryItem } from '../redux/inventorySlice';
import { MealSchedulePreferences, DayPreference, MealDietType, DEFAULT_DAY_PREFERENCE, DEFAULT_MEAL_SCHEDULE, MealFrequencyPreferences } from '../redux/userPreferencesSlice';
import { TasteProfile } from '../redux/feedbackSlice';
import { DietType } from '../types/recipe';
import { calculateTasteProfileBonus } from './tastePreferenceMatching';
import { normalizeAllergenText, recipeHasAllergen } from './allergenMatcher';
import { tasteAxesBonus, type TasteAxes } from './tasteAxes';
import { detectProtein, isVegetarianRecipe, isFishRecipe, satisfiesDietaryRestrictions } from '../utils/dietClassifier';
import {
  WeekBalance, narrowForSlot, isSlowCookerRecipe, isPrepAheadRecipe, busyEveningMinutes, escapesTimeCap, type PlanSlot,
} from './planRanking';

export { isSlowCookerRecipe, isPrepAheadRecipe };
import { isDessertRecipe } from '../utils/recipeClassification';
import { DISH_FORMAT_KEYWORDS, SameMealLedger, mealIdentityOf, type DatedMeal } from './mealIdentity';
import { pantryMatcher, shelfMatcher, type PantryMatcher } from './pantryMatch';
import { hasIngredients } from '../utils/recipeContent';

// The protein/diet classifiers now live in the shared `dietClassifier` module so
// the client pills, the generator, and the `api/` routes share one source of
// truth. Re-exported here to preserve this service's public API and tests.
export { detectProtein, isVegetarianRecipe, isFishRecipe } from '../utils/dietClassifier';

const BEVERAGE_TITLE_RE =
  /\b(cocktail|mocktail|martini|margarita|sangria|mojito|daiquiri|negroni|mimosa|bellini|paloma|pi[nñ]a colada|cosmopolitan|aperol|spritz|smoothie|juice|lemonade|tea|coffee|beer|wine|cider|whiskey|vodka|rum|gin|milkshake|slushie|latte|frappe|kombucha|lassi|punch|kefir)\b/i;

export interface WeekSlot {
  date: string; // YYYY-MM-DD
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  dayName: string;
  mealType: MealType;
  isWeekend: boolean;
}

export interface GeneratedMeal {
  slot: WeekSlot;
  recipe: PersonalRecipeIndexEntry;
  score: number;
  dietaryRole?: DietaryRole; // If this meal was reserved for a dietary requirement
  matchedDeals?: LufaDeal[]; // Deals that this recipe uses
}

export interface GenerationContext {
  previousWeekRecipeIds: string[];
  usedRecipeIds: Set<string>;
  usedProteins: Map<string, number>; // protein -> count
  usedCuisines: Map<string, number>; // cuisine -> count
  usedDishFormats: Map<string, number>; // dish format (tacos, soup, etc.) -> count
  usedKeyFlavors: Map<string, number>; // key flavor (coconut, peanut, etc.) -> count
  metadata: Record<string, RecipeMetadata>;
  selectedDeals?: LufaDeal[]; // Deals to boost in scoring
  inventory?: InventoryItem[]; // Current inventory items for matching
}

/**
 * Dietary preferences for meal generation
 */
export interface DietaryConfig {
  includeVegetarian: boolean; // At least one vegetarian meal
  includeFish: boolean; // At least one fish/seafood meal
}

/**
 * Dietary role for a reserved meal slot
 */
export type DietaryRole = 'vegetarian' | 'fish' | 'meat';

/**
 * Badge type for dinner MVP
 */
export type DinnerBadge = 'fish' | 'veggie' | 'quick' | 'slowcooker';

/**
 * Dinner slot type for MVP
 */
export type DinnerSlotType = 'quick' | 'weekend' | 'fish' | 'vegetarian' | 'pasta' | 'meat' | 'any';

/**
 * Dinner slot for the 7-dinner MVP plan
 */
export interface DinnerSlot {
  date: string;           // YYYY-MM-DD
  dayOfWeek: number;      // 0-6 (Sunday-Saturday)
  dayName: string;
  slotType: DinnerSlotType;
  maxCookTime?: number;   // Minutes, undefined = no limit
  allowPrepAhead?: boolean; // Prep-ahead/slow-cooker meals bypass time constraint
  kidsPresent?: boolean;  // Future: kid-friendly filters
}

/**
 * Config for dinner-only MVP generation
 */
export interface DinnerMVPConfig {
  excludedRecipeIds: string[];  // From last confirmed week
  preferredServings?: number;
  inventory?: InventoryItem[]; // Current inventory items for matching
  todayOverride?: string; // YYYY-MM-DD, for testing — defaults to actual today
  mealSchedule?: MealSchedulePreferences; // User-defined day preferences
  deals?: LufaDeal[]; // Current Lufa deals for scoring boost + freshness
  recentRecipeHistory?: Record<string, number>; // recipeId → weeksAgo (1-3), graduated penalties
  previouslyGeneratedRecipeIds?: string[]; // Recipes from current unconfirmed plan (hard disqualify on re-generate)
  tasteProfile?: TasteProfile | null; // Learned taste preferences for scoring boost
  dietaryRestrictions?: DietType[]; // Hard filter — exclude recipes that don't satisfy all restrictions
  dislikedIngredients?: string[]; // Hard filter — exclude recipes containing any of these ingredients
  /** Already-normalised allergen terms (see allergenMatcher.buildAllergenTerms) — hard filter, word-anchored */
  allergenTerms?: string[];
  /** The onboarding taste profile; adds a bonus on top of scoreDinnerRecipe when present */
  tasteAxes?: TasteAxes | null;
  mealFrequencyPreferences?: MealFrequencyPreferences; // Promote 'any' slots to vegetarian/fish to hit weekly targets
  /**
   * Dinners the picks must not repeat (#736): the past two weeks, what is
   * already planned around the slots being filled, and — across a horizon that
   * spans two Monday weeks — the first week's picks. See `mealIdentity`.
   */
  sameMealHistory?: DatedMeal[];
  /** YYYY-MM-DD, inclusive. Slots after it are not generated at all. */
  untilDate?: string;
}

/**
 * Ingredient coverage breakdown — classifies each ingredient as Lufa deal, pantry, or to-buy
 */
export interface IngredientCoverage {
  totalCount: number;       // Total recipe ingredients
  lufaCount: number;        // Matched to current Lufa deals
  pantryCount: number;      // Matched to user inventory + pantry staples
  toBuyCount: number;       // Not matched anywhere
  coveragePercent: number;  // Math.round((lufaCount + pantryCount) / totalCount * 100)
  lufaItems: string[];      // Display names of Lufa-matched ingredients
  pantryItems: string[];    // Display names of pantry-matched ingredients
  toBuyItems: string[];     // Display names of items to buy
}

/**
 * Generated dinner for MVP
 */
/**
 * Match metadata explaining why a recipe was selected
 */
export interface MatchMetadata {
  inventoryMatchPercent?: number; // 0-100
  usesExpiringIngredients?: string[]; // Names of expiring items used
  isNewRecipe?: boolean; // Never planned before
  excitementScore?: number;
  constraintLabel?: string; // e.g. "Vegetarian", "Quick meal", "Pasta", "Fish"
  dealCount?: number; // Number of Lufa deals matched
  tasteProfileBonus?: number; // Score adjustment from taste profile preferences
  vedetteIngredient?: string; // Star delivery ingredient (e.g. Lufa item)
  ingredientCoverage?: IngredientCoverage;
}

export interface GeneratedDinner {
  slot: DinnerSlot;
  recipe: PersonalRecipeIndexEntry;
  score: number;
  badges: DinnerBadge[];
  matchMetadata?: MatchMetadata;
  matchedDeals?: LufaDeal[]; // Deals that this recipe's ingredients match
}

// Cuisine categories
const CUISINE_KEYWORDS: Record<string, string[]> = {
  italian: ['italian', 'italien', 'pasta', 'risotto', 'pizza', 'lasagna', 'spaghetti', 'penne', 'gnocchi'],
  mexican: ['mexican', 'mexicain', 'taco', 'burrito', 'enchilada', 'quesadilla', 'salsa', 'guacamole'],
  asian: ['asian', 'asiatique', 'chinese', 'chinois', 'japanese', 'japonais', 'korean', 'coréen', 'thai', 'thaï', 'vietnamese', 'vietnamien', 'stir fry', 'sauté', 'teriyaki', 'curry'],
  indian: ['indian', 'indien', 'curry', 'tikka', 'masala', 'biryani', 'naan', 'tandoori'],
  mediterranean: ['mediterranean', 'méditerranéen', 'greek', 'grec', 'hummus', 'falafel', 'tzatziki'],
  french: ['french', 'français', 'coq au vin', 'ratatouille', 'quiche', 'cassoulet', 'bouillabaisse'],
  american: ['american', 'américain', 'burger', 'bbq', 'barbecue', 'mac and cheese', 'grilled'],
  middle_eastern: ['middle eastern', 'moyen-orient', 'shawarma', 'kebab', 'tabouleh', 'fattoush'],
};

// Dish format categories (matched against title only) live with the
// same-meal rules, which read them too.

// Key flavoring ingredients (matched against title)
const KEY_FLAVOR_KEYWORDS: Record<string, string[]> = {
  coconut: ['coconut', 'coco', 'noix de coco', 'lait de coco', 'coconut milk'],
  peanut: ['peanut', 'arachide', 'peanut butter', 'beurre d\'arachide', 'satay'],
  honey: ['honey', 'miel'],
  teriyaki: ['teriyaki'],
  bbq: ['bbq', 'barbecue'],
  lemon: ['lemon', 'citron'],
  sesame: ['sesame', 'sésame'],
  maple: ['maple', 'érable'],
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Title-based meal-type heuristics.
 *
 * BREAKFAST: morning-only signals (used as a positive filter for breakfast and
 * a negative filter at lunch/dinner).
 *
 * DINNER: lunch/dinner signals — proteins, dish formats, cuisine markers. Used
 * as a positive filter for lunch/dinner with empty dishTypes, and as a
 * negative filter at breakfast to disqualify clearly non-breakfast dishes
 * (e.g. Poke Bowl, Fettuccine).
 */
const BREAKFAST_TITLE_PATTERN =
  /pancake|waffle|omelette|eggs|toast|muffin|smoothie|granola|porridge|acai|cereal|oatmeal|breakfast|brioche|crêpes|pain doré/i;
const DINNER_TITLE_PATTERN =
  /\b(chicken|beef|salmon|pork|lamb|shrimp|fish|tofu|pasta|stew|curry|roast|bake[ds]?|casserole|soup|rice|noodles?|stir.?fry|lasagna|risotto|chili|tacos?|burrito|enchilada|burger|meat|sausage|turkey|duck|veal|lobster|crab|scallop|filet|fillet|gratin|braise[ds]?|ragout|ragu|bolognese|carbonara|primavera|alfredo|pot pie|shepherd|stroganoff|goulash|tagine|biryani|tikka|masala|vindaloo|korma|teriyaki|katsu|ramen|pho|pad thai|fried rice|lo mein|chow mein|jambalaya|gumbo|paella|coq au vin|bourguignon|blanquette|pot-au-feu|poulet|boeuf|porc|agneau|saumon|crevettes|poisson|fettuccine|spaghetti|linguine|penne|rigatoni|macaroni|gnocchi|ravioli|tortellini|poke|sushi|sashimi)\b/i;

/**
 * Deal match result with confidence scoring
 */
export interface DealMatchResult extends LufaDeal {
  confidence: 'high' | 'medium' | 'low';
}

/**
 * FR -> EN translation dictionary for ingredient matching.
 * Includes singular, plural, and common culinary terms.
 */
const FR_EN_TRANSLATIONS: Record<string, string[]> = {
  // Proteins
  poulet: ['chicken'], boeuf: ['beef'], porc: ['pork'],
  saumon: ['salmon'], crevette: ['shrimp'], crevettes: ['shrimp'],
  poisson: ['fish'], agneau: ['lamb'], dinde: ['turkey'],
  canard: ['duck'], veau: ['veal'], truite: ['trout'],
  tilapia: ['tilapia'], morue: ['cod'], thon: ['tuna'],
  pétoncle: ['scallop'], pétoncles: ['scallops'],
  homard: ['lobster'], crabe: ['crab'],
  saucisse: ['sausage'], saucisses: ['sausages'],
  merguez: ['merguez', 'sausage'], bacon: ['bacon'],
  jambon: ['ham'], tofu: ['tofu'], tempeh: ['tempeh'],

  // Cuts and preparations
  filet: ['fillet', 'tenderloin'], filets: ['fillets', 'tenderloins'],
  cuisse: ['thigh'], cuisses: ['thighs'],
  poitrine: ['breast'], poitrines: ['breasts'],
  haché: ['ground'], côtelette: ['chop'], côtelettes: ['chops'],
  désossé: ['boneless'], désossée: ['boneless'], désossées: ['boneless'],

  // Produce - vegetables
  tomate: ['tomato'], tomates: ['tomatoes', 'tomato'],
  cerises: ['cherry'],
  carotte: ['carrot'], carottes: ['carrots', 'carrot'],
  oignon: ['onion'], oignons: ['onions', 'onion'],
  ail: ['garlic'],
  épinard: ['spinach'], épinards: ['spinach'],
  brocoli: ['broccoli'],
  laitue: ['lettuce'],
  champignon: ['mushroom'], champignons: ['mushrooms', 'mushroom'],
  courgette: ['zucchini'], courgettes: ['zucchini'],
  aubergine: ['eggplant'],
  poivron: ['pepper', 'bell pepper'], poivrons: ['peppers', 'bell peppers'],
  céleri: ['celery'],
  concombre: ['cucumber'],
  chou: ['cabbage'],
  asperge: ['asparagus'], asperges: ['asparagus'],
  haricot: ['bean'], haricots: ['beans', 'bean'],
  pois: ['peas'],
  radis: ['radish'],
  betterave: ['beet'], betteraves: ['beets', 'beet'],
  maïs: ['corn'],
  fenouil: ['fennel'],
  patate: ['potato'], patates: ['potatoes', 'potato'],

  // Produce - fruits
  pomme: ['apple'], pommes: ['apples', 'apple'],
  poire: ['pear'], poires: ['pears', 'pear'],
  fraise: ['strawberry'], fraises: ['strawberries', 'strawberry'],
  framboise: ['raspberry'], framboises: ['raspberries', 'raspberry'],
  bleuet: ['blueberry'], bleuets: ['blueberries', 'blueberry'],
  pêche: ['peach'], pêches: ['peaches', 'peach'],
  mangue: ['mango'],
  ananas: ['pineapple'],
  citron: ['lemon'],
  lime: ['lime'],
  avocat: ['avocado'],
  banane: ['banana'],
  raisin: ['grape'], raisins: ['grapes', 'grape'],
  melon: ['melon'],

  // Herbs
  basilic: ['basil'], coriandre: ['cilantro', 'coriander'],
  persil: ['parsley'], menthe: ['mint'], thym: ['thyme'],
  romarin: ['rosemary'], origan: ['oregano'],

  // Dairy
  fromage: ['cheese'], lait: ['milk'], crème: ['cream'],
  beurre: ['butter'], yogourt: ['yogurt'], yaourt: ['yogurt'],
  oeuf: ['egg'], oeufs: ['eggs', 'egg'],

  // Bakery
  pain: ['bread'], levain: ['sourdough'],

  // Descriptors (for compound names)
  frais: ['fresh'], fraîche: ['fresh'], fraîches: ['fresh'],
  biologique: ['organic'], bio: ['organic'],
  sauvage: ['wild'], sauvages: ['wild'],
  fumé: ['smoked'], fumée: ['smoked'],
  mariné: ['marinated'], marinée: ['marinated'],
  grillé: ['grilled'], grillée: ['grilled'],
  rôti: ['roasted'], rôtie: ['roasted'],
  mélange: ['mix', 'blend', 'mixed'],
  vieilli: ['aged'],
};

/**
 * Common pantry staples that most kitchens have on hand (FR + EN).
 * Used by classifyIngredients() to avoid counting these as "to buy".
 */
export const PANTRY_STAPLES = [
  // Salt & pepper
  'salt', 'sel', 'pepper', 'poivre', 'black pepper', 'poivre noir',
  // Oils & fats
  'oil', 'huile', 'olive oil', "huile d'olive", 'vegetable oil', 'huile végétale',
  'butter', 'beurre',
  // Aromatics
  'garlic', 'ail', 'ginger', 'gingembre',
  // Flour & starch
  'flour', 'farine', 'cornstarch', 'fécule',
  // Sweeteners
  'sugar', 'sucre', 'honey', 'miel', 'maple syrup', "sirop d'érable",
  // Acids
  'vinegar', 'vinaigre', 'lemon', 'citron', 'lime',
  // Sauces & condiments
  'soy sauce', 'sauce soya', 'ketchup', 'mustard', 'moutarde',
  'mayonnaise', 'tabasco', 'sambal',
  // Dried herbs & spices
  'oregano', 'origan', 'thyme', 'thym', 'cumin', 'paprika',
  'cinnamon', 'cannelle', 'chili powder', 'poudre de chili',
  'bay leaf', 'laurier', 'basil', 'basilic', 'coriandre', 'cilantro',
  'persil', 'parsley', 'piment', 'chili',
  // Dairy staples
  'milk', 'lait',
  // Stock
  'broth', 'bouillon', 'stock',
  // Pasta & rice (dry goods)
  'pasta', 'pâtes', 'rice', 'riz', 'noodle', 'nouille',
];

/**
 * Normalize a French word: strip accents, lowercase, remove trailing plural 's'
 */
function normalizeFrench(word: string): string {
  return word
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/s$/, ''); // strip trailing 's' (French plural)
}

// Common words to exclude from matching (too generic)
const DEAL_STOP_WORDS = new Set([
  // French common words
  'bio', 'biologique', 'biologiques', 'frais', 'fraiche', 'fraiches',
  'local', 'locaux', 'locale', 'locales', 'ferme', 'fermes',
  'petit', 'petite', 'petits', 'petites', 'grand', 'grande', 'grands', 'grandes',
  'rouge', 'rouges', 'vert', 'verte', 'verts', 'vertes', 'blanc', 'blanche', 'blancs', 'blanches',
  'noir', 'noire', 'noirs', 'noires', 'jaune', 'jaunes',
  'doux', 'douce', 'fort', 'forte', 'forts', 'fortes',
  'avec', 'sans', 'pour', 'dans', 'entre', 'sous', 'sur',
  // English common words
  'fresh', 'local', 'farm', 'farms', 'organic', 'small', 'large', 'mini',
  'red', 'green', 'white', 'black', 'yellow', 'orange',
  'sweet', 'mild', 'hot', 'spicy', 'mild',
  'pack', 'bundle', 'bunch', 'bag', 'box',
  'mix', 'mixed', 'assorted', 'variety',
  'cup', 'cups', 'tablespoon', 'tablespoons', 'teaspoon', 'teaspoons',
  'pound', 'pounds', 'ounce', 'ounces', 'gram', 'grams',
  'sliced', 'diced', 'chopped', 'minced', 'crushed', 'ground',
  // Words that cause false matches
  'sauce', 'sauces', 'huile', 'oil', 'oils', 'vinegar', 'vinaigre',
  'sure', 'sour', 'cream', 'creme', 'fraiche', 'fraîche',
  'sugar', 'sucre', 'salt', 'sel', 'pepper', 'poivre',
  'water', 'eau', 'stock', 'broth', 'bouillon',
  'flour', 'farine', 'butter', 'beurre',
]);

// Ingredients that should NOT be matched to Lufa deals (sauces, spices, condiments)
const EXCLUDED_INGREDIENT_PATTERNS = [
  /sauce/i, /soy\s*sauce/i, /fish\s*sauce/i, /oyster\s*sauce/i, /hot\s*sauce/i,
  /vinegar/i, /vinaigre/i,
  /oil$/i, /huile/i, /olive/i,
  /spice/i, /épice/i, /seasoning/i, /assaisonnement/i,
  /salt$/i, /sel$/i, /pepper$/i, /poivre$/i,
  /sugar$/i, /sucre$/i,
  /flour$/i, /farine$/i,
  /stock$/i, /broth$/i, /bouillon/i,
  /paste$/i, /pâte$/i,
  /powder$/i, /poudre$/i,
  /extract$/i, /extrait$/i,
  /mustard/i, /moutarde/i,
  /ketchup/i, /mayo/i, /mayonnaise/i,
  /soy$/i, /soya$/i,
];

/**
 * Per-ingredient deal match result
 */
export interface IngredientDealMatch {
  ingredient: string;
  ingredientIndex: number;
  deal: LufaDeal;
  confidence: 'high' | 'medium';
}

/**
 * Check if an ingredient should be excluded from Lufa matching
 * (sauces, spices, condiments, oils, etc.)
 */
function shouldExcludeIngredient(ingredient: string): boolean {
  return EXCLUDED_INGREDIENT_PATTERNS.some(pattern => pattern.test(ingredient));
}

/**
 * Extract the core food keyword from an ingredient string.
 * E.g., "2 cups diced chicken breast" -> "chicken"
 * E.g., "crème sure fraîche" -> "creme sure fraiche" (as one unit)
 */
function extractCoreKeywords(ingredient: string): string[] {
  const lower = ingredient.toLowerCase();
  const normalized = normalizeFrench(lower);

  // Remove quantities and units
  const withoutQuantity = normalized
    .replace(/^[\d\s½¼¾⅓⅔⅛⅜⅝⅞\/.-]+/, '') // Remove leading numbers/fractions
    .replace(/\b(cups?|tbsp|tsp|tablespoons?|teaspoons?|oz|lb|lbs|g|kg|ml|l|pounds?|ounces?|grams?)\b/gi, '')
    .trim();

  // Split into words and filter
  return withoutQuantity
    .split(/\s+/)
    .filter(w => w.length >= 4 && !DEAL_STOP_WORDS.has(w));
}

/**
 * Match each ingredient to its best deal (if any).
 * Returns at most one match per ingredient, giving bounded results.
 *
 * STRICT MATCHING RULES:
 * - Excludes sauces, spices, condiments, oils by default
 * - Requires keyword length >= 5 characters for matching
 * - Only matches core food items, not modifiers
 */
export function matchIngredientsToDeals(
  ingredients: string[],
  deals: LufaDeal[]
): IngredientDealMatch[] {
  if (!deals || deals.length === 0 || !ingredients || ingredients.length === 0) {
    return [];
  }

  const matches: IngredientDealMatch[] = [];

  for (let i = 0; i < ingredients.length; i++) {
    const ingredient = ingredients[i];

    // Skip excluded categories (sauces, spices, etc.)
    if (shouldExcludeIngredient(ingredient)) {
      continue;
    }

    const ingredientLower = ingredient.toLowerCase();
    const ingredientKeywords = extractCoreKeywords(ingredient);

    // Skip if no meaningful keywords extracted
    if (ingredientKeywords.length === 0) {
      continue;
    }

    let bestMatch: { deal: LufaDeal; confidence: 'high' | 'medium' } | null = null;

    for (const deal of deals) {
      // Extract meaningful keywords from deal names (require 5+ chars for stricter matching)
      const dealKeywords = [
        ...(deal.name || '').toLowerCase().split(/\s+/),
        ...(deal.nameFr || '').toLowerCase().split(/\s+/),
      ].filter(k => k.length >= 5 && !DEAL_STOP_WORDS.has(k));

      // Also get normalized versions
      const dealKeywordsNormalized = dealKeywords.map(k => normalizeFrench(k));

      for (const ingKeyword of ingredientKeywords) {
        // Only match if ingredient keyword is substantial (5+ chars)
        if (ingKeyword.length < 5) continue;

        for (let j = 0; j < dealKeywords.length; j++) {
          const dealKeyword = dealKeywords[j];
          const dealKeywordNorm = dealKeywordsNormalized[j];

          // Strategy 1: Direct match (high confidence)
          // Require keyword to match as a whole word, not substring
          if (ingKeyword === dealKeyword || ingKeyword === dealKeywordNorm) {
            bestMatch = { deal, confidence: 'high' };
            break;
          }

          // Strategy 2: One contains the other (if both are long enough)
          if (ingKeyword.length >= 5 && dealKeyword.length >= 5) {
            if (ingKeyword.includes(dealKeyword) || dealKeyword.includes(ingKeyword)) {
              bestMatch = { deal, confidence: 'high' };
              break;
            }
          }

          // Strategy 3: Translation match
          const translations = FR_EN_TRANSLATIONS[dealKeyword] || FR_EN_TRANSLATIONS[dealKeywordNorm] || [];
          if (translations.some(t => t.length >= 5 && (ingKeyword.includes(t) || t.includes(ingKeyword)))) {
            if (!bestMatch || bestMatch.confidence !== 'high') {
              bestMatch = { deal, confidence: 'medium' };
            }
          }
        }

        if (bestMatch?.confidence === 'high') break;
      }

      if (bestMatch?.confidence === 'high') break;
    }

    if (bestMatch) {
      matches.push({
        ingredient,
        ingredientIndex: i,
        deal: bestMatch.deal,
        confidence: bestMatch.confidence,
      });
    }
  }

  return matches;
}

/**
 * Match a recipe's ingredients against selected deals.
 * Returns DealMatchResult[] with confidence scoring.
 *
 * NOTE: This returns ALL deals that match ANY ingredient.
 * For UI display (badges, counts), prefer matchIngredientsToDeals() instead.
 *
 * Matching strategies (in priority order):
 * 1. Direct keyword match in recipe text (high confidence)
 * 2. FR->EN translation match (high confidence)
 * 3. Fuzzy/normalized match with plural/accent stripping (medium confidence)
 */
export function matchRecipeToDeals(
  recipe: PersonalRecipeIndexEntry,
  deals: LufaDeal[]
): DealMatchResult[] {
  if (!deals || deals.length === 0) return [];

  const matchedDeals: DealMatchResult[] = [];

  // Build searchable text from recipe title and ingredients
  const searchText = [
    recipe.title.toLowerCase(),
    ...recipe.ingredients.map(i => i.toLowerCase()),
  ].join(' ');

  // Also build normalized (accent-stripped, singular) version for fuzzy matching
  const normalizedSearchText = normalizeFrench(searchText);

  for (const deal of deals) {
    // Extract meaningful keywords from deal names (FR + EN)
    // Skip short words (<4 chars) and common stopwords
    const dealKeywords = [
      ...(deal.name || '').toLowerCase().split(/\s+/),
      ...(deal.nameFr || '').toLowerCase().split(/\s+/),
    ].filter(k => k.length >= 4 && !DEAL_STOP_WORDS.has(k));

    let bestConfidence: 'high' | 'medium' | 'low' | null = null;

    for (const keyword of dealKeywords) {
      // Strategy 1: Direct match (high confidence)
      if (searchText.includes(keyword)) {
        bestConfidence = 'high';
        break; // Can't do better
      }

      // Strategy 2: Translation match (high confidence)
      const translations = FR_EN_TRANSLATIONS[keyword] || [];
      if (translations.some(t => searchText.includes(t))) {
        bestConfidence = 'high';
        break;
      }

      // Strategy 3: Normalized fuzzy match — strip accents + plurals (medium confidence)
      const normalizedKeyword = normalizeFrench(keyword);
      if (normalizedKeyword.length > 2 && normalizedSearchText.includes(normalizedKeyword)) {
        if (!bestConfidence || (bestConfidence as string) === 'low') {
          bestConfidence = 'medium';
        }
      }

      // Strategy 3b: Check normalized translations
      const normalizedTranslations = FR_EN_TRANSLATIONS[normalizedKeyword] || [];
      if (normalizedTranslations.some(t => searchText.includes(t))) {
        if (!bestConfidence || (bestConfidence as string) === 'low') {
          bestConfidence = 'medium';
        }
      }
    }

    if (bestConfidence) {
      matchedDeals.push({
        ...deal,
        confidence: bestConfidence,
      });
    }
  }

  return matchedDeals;
}

/**
 * Merge two recipe arrays, deduplicating by id
 */
function deduplicateRecipes(
  base: PersonalRecipeIndexEntry[],
  additional: PersonalRecipeIndexEntry[]
): PersonalRecipeIndexEntry[] {
  const ids = new Set(base.map(r => r.id));
  const merged = [...base];
  for (const recipe of additional) {
    if (!ids.has(recipe.id)) {
      merged.push(recipe);
      ids.add(recipe.id);
    }
  }
  return merged;
}

/**
 * Find deal-optimized recipes — recipes that match ≥2 deal ingredients.
 * These should be injected into the candidate pool even if they wouldn't normally score highly.
 */
export function findDealOptimizedRecipes(
  recipes: PersonalRecipeIndexEntry[],
  deals: LufaDeal[]
): PersonalRecipeIndexEntry[] {
  if (!deals || deals.length === 0) return [];

  return recipes.filter(recipe => {
    const matches = matchRecipeToDeals(recipe, deals);
    return matches.length >= 2;
  });
}

/**
 * Calculate deal bonus score for a recipe based on number of deal matches.
 * Includes extra tier bonus for 3+ matches.
 *
 * @param matchCount - Number of deals matched
 * @param proteinDealCount - Number of protein deals matched (extra bonus)
 */
export function calculateDealBonus(matchCount: number, proteinDealCount: number = 0): number {
  if (matchCount === 0) return 0;

  let bonus = 50; // Base bonus for having any deal match
  bonus += matchCount * 15; // Per-deal bonus

  // Extra tier bonus for 3+ matches (deal-optimized recipe)
  if (matchCount >= 3) {
    bonus += 40;
  }

  // Protein deal bonus
  bonus += proteinDealCount * 20;

  return bonus;
}

/**
 * Calculate freshness time-decay bonus for perishable Lufa deals.
 * Perishable items (produce, dairy) get higher bonus Mon-Wed when freshest.
 * Non-perishable items (protein that can be frozen, prepared, bakery, other) get no bonus.
 *
 * @param dayOfWeek - 0=Sunday, 1=Monday, ..., 6=Saturday
 * @param dealCategory - The Lufa deal category
 */
export function calculateFreshnessBonus(
  dayOfWeek: number,
  dealCategory: LufaDeal['category']
): number {
  // Only perishable categories get freshness bonus
  const perishableCategories: LufaDeal['category'][] = ['produce', 'dairy'];
  if (!perishableCategories.includes(dealCategory)) {
    return 0;
  }

  // Freshness bonus only applies Mon-Fri (weekdays)
  // Weekend (Sat=6, Sun=0) gets no bonus
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return 0;
  }

  // Time-decay: highest on Monday, declining through the week
  // Mon=30, Tue=25, Wed=20, Thu=10, Fri=5
  const bonusByDay: Record<number, number> = {
    1: 30, // Monday
    2: 25, // Tuesday
    3: 20, // Wednesday
    4: 10, // Thursday
    5: 5,  // Friday
  };

  return bonusByDay[dayOfWeek] || 0;
}

/**
 * Generate all meal slots for a week starting from the given Monday.
 * Returns 21 slots: Breakfast, Lunch, Dinner for every day. Callers filter
 * these down to the user's selected slots, so breakfast is only kept on days
 * the user actually chose it (incl. weekdays) — never forced.
 */
export function generateWeekSlots(weekStartDate: string): WeekSlot[] {
  const slots: WeekSlot[] = [];
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const dateStr = addDays(weekStartDate, dayOffset);
    const dayOfWeek = parseIsoDate(dateStr).getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const dayName = DAY_NAMES[dayOfWeek];

    // Breakfast, Lunch, Dinner for every day; callers filter to selected slots.
    const mealTypes: MealType[] = ['breakfast', 'lunch', 'dinner'];

    for (const mealType of mealTypes) {
      slots.push({
        date: dateStr,
        dayOfWeek,
        dayName,
        mealType,
        isWeekend,
      });
    }
  }

  return slots;
}

/**
 * Inventory match result for recipe scoring
 */
export interface InventoryMatchResult {
  score: number; // 0-1, percentage of ingredients matched
  matchedCount: number;
  totalCount: number;
  expiringIngredients: string[];
}

/**
 * Context for excitement scoring - tracks what's already in the week
 */
export interface ExcitementContext {
  usedCuisinesThisWeek: Set<string>;
  tasteProfile?: TasteProfile | null;
}

/**
 * Calculate excitement score for a recipe.
 * Considers rating, novelty, favorites, and cuisine variety.
 * Returns a bonus score (0+) to be added to the base recipe score.
 */
export function calculateExcitementScore(
  recipe: PersonalRecipeIndexEntry,
  metadata: RecipeMetadata | undefined,
  context: ExcitementContext
): number {
  let excitement = 0;

  // === RATING BONUS ===
  // Higher-rated recipes are more exciting
  if (metadata?.rating) {
    // rating 1-5: score bonus from -20 to +20
    excitement += (metadata.rating - 3) * 10;
    // Extra bonus for 5-star recipes
    if (metadata.rating === 5) {
      excitement += 10;
    }
  }

  // === NOVELTY BONUS ===
  // Never-planned or rarely-planned recipes are exciting
  // Moderate usage is fine; overuse reduces excitement
  if (!metadata || metadata.timesPlanned === 0) {
    excitement += 25; // Never tried = exciting!
  } else if (metadata.timesPlanned <= 2) {
    excitement += 15; // Still relatively new
  } else if (metadata.timesPlanned <= 7) {
    excitement += 5; // Comfortable rotation
  } else if (metadata.timesPlanned >= 15) {
    excitement -= 20; // Very overused
  } else {
    excitement -= 10; // Getting stale (8-14)
  }

  // === FAVORITE BONUS ===
  if (metadata?.isFavorite) {
    excitement += 15;
  }

  // === CUISINE VARIETY BONUS ===
  // Introducing a new cuisine to the week is exciting
  const cuisine = detectCuisine(recipe);
  if (cuisine) {
    if (!context.usedCuisinesThisWeek.has(cuisine)) {
      excitement += 15; // New cuisine this week
    }
    // No penalty for repeat cuisine here — that's handled elsewhere
  }

  // === TASTE PROFILE BONUS ===
  // Apply learned taste preferences when a profile exists
  if (context.tasteProfile) {
    excitement += calculateTasteProfileBonus(recipe, context.tasteProfile);
  }

  return Math.max(0, excitement);
}

/**
 * Calculate days until expiry
 */
function getDaysUntilExpiry(
  expiryDate: { day: number; month: number; year: number },
  fromDate: Date = new Date()
): number {
  const expiry = new Date(expiryDate.year, expiryDate.month - 1, expiryDate.day);
  const diffTime = expiry.getTime() - fromDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * The pantry as the recipe screen sees it (`pantryMatch.ts`), kept per
 * inventory array: the planner scores every recipe against the same array, so
 * the index is built once per generation. This used to be a private word-overlap
 * test that disagreed with every screen — "pomme" matched "pommes de terre",
 * and a French shelf never matched an English recipe.
 */
const inventoryMatcher = (inventory: InventoryItem[]) => shelfMatcher(inventory);

/**
 * Calculate inventory match score for a recipe
 * Used in scoring to prioritize recipes that use ingredients from current inventory
 */
export function calculateInventoryMatchScore(
  recipe: PersonalRecipeIndexEntry,
  inventory: InventoryItem[]
): InventoryMatchResult {
  const ingredientList = recipe.ingredients || [];

  if (ingredientList.length === 0) {
    return {
      score: 0,
      matchedCount: 0,
      totalCount: 0,
      expiringIngredients: [],
    };
  }

  // Only active items count; `inventoryMatcher` drops the rest.
  const shelf = inventoryMatcher(inventory);

  let matchedCount = 0;
  const expiringMatches: string[] = [];
  const now = new Date();

  ingredientList.forEach(ingredient => {
    const inventoryItem = shelf.itemsForLine(ingredient)[0];
    if (!inventoryItem) return;
    matchedCount++;

    // Check if expiring within 5 days
    if (inventoryItem.expiryDate) {
      const daysUntilExpiry = getDaysUntilExpiry(inventoryItem.expiryDate, now);
      if (daysUntilExpiry <= 5 && daysUntilExpiry >= 0) {
        expiringMatches.push(inventoryItem.name);
      }
    }
  });

  const totalCount = ingredientList.length;

  return {
    score: totalCount > 0 ? matchedCount / totalCount : 0,
    matchedCount,
    totalCount,
    expiringIngredients: expiringMatches,
  };
}

/**
 * Classify each recipe ingredient as coming from Lufa deals, pantry/inventory, or needing purchase.
 * Uses existing deal match results and inventory matching, plus pantry staples fallback.
 */
export function classifyIngredients(
  recipe: PersonalRecipeIndexEntry,
  dealMatches: IngredientDealMatch[],
  inventory: InventoryItem[],
): IngredientCoverage {
  const ingredientList = recipe.ingredients || [];

  if (ingredientList.length === 0) {
    return {
      totalCount: 0, lufaCount: 0, pantryCount: 0, toBuyCount: 0,
      coveragePercent: 0, lufaItems: [], pantryItems: [], toBuyItems: [],
    };
  }

  const lufaItems: string[] = [];
  const pantryItems: string[] = [];
  const toBuyItems: string[] = [];

  // Build set of ingredient indices matched to deals for fast lookup
  const dealMatchedIndices = new Set(dealMatches.map(dm => dm.ingredientIndex));

  const shelf = inventoryMatcher(inventory);

  ingredientList.forEach((ingredient, index) => {
    // 1. Check if matched to a Lufa deal
    if (dealMatchedIndices.has(index)) {
      lufaItems.push(ingredient);
      return;
    }

    // 2. Check if matched to inventory
    if (shelf.itemsForLine(ingredient).length > 0) {
      pantryItems.push(ingredient);
      return;
    }

    // 3. Check if it's a pantry staple
    const ingredientLower = ingredient.toLowerCase();
    const isPantryStaple = PANTRY_STAPLES.some(staple => ingredientLower.includes(staple));
    if (isPantryStaple) {
      pantryItems.push(ingredient);
      return;
    }

    // 4. Otherwise → to buy
    toBuyItems.push(ingredient);
  });

  const totalCount = ingredientList.length;
  const coveredCount = lufaItems.length + pantryItems.length;

  return {
    totalCount,
    lufaCount: lufaItems.length,
    pantryCount: pantryItems.length,
    toBuyCount: toBuyItems.length,
    coveragePercent: totalCount > 0 ? Math.round((coveredCount / totalCount) * 100) : 0,
    lufaItems,
    pantryItems,
    toBuyItems,
  };
}

// === DELIVERY BONUS CONFIGURATION ===
const DELIVERY_BONUS_PER_MATCH = 50;      // Extra bonus per matched delivery ingredient (on top of inventory bonus)
const DELIVERY_PERISHABLE_BONUS = 25;      // Extra for perishable delivery items (produce, dairy)
const DELIVERY_CLUSTER_THRESHOLD = 3;      // Min delivery matches for cluster bonus
const DELIVERY_CLUSTER_BONUS = 60;         // Bonus for recipes using 3+ delivery items
const DELIVERY_WINDOW_DAYS = 7;            // Only items added within this window get bonus

const PERISHABLE_CATEGORIES = new Set(['Vegetables', 'Fruits', 'Dairy', 'Meat']);

function isRecentDeliveryItem(item: InventoryItem): boolean {
  if (item.source !== 'lufa-delivery') return false;
  const addedDate = new Date(item.dateAdded);
  const now = new Date();
  const daysSinceAdded = (now.getTime() - addedDate.getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceAdded <= DELIVERY_WINDOW_DAYS;
}

/** The recent Lufa delivery as its own shelf, matched like the whole pantry. */
const deliveryMatchers = new WeakMap<InventoryItem[], PantryMatcher<InventoryItem>>();
function deliveryMatcher(inventory: InventoryItem[]): PantryMatcher<InventoryItem> {
  let matcher = deliveryMatchers.get(inventory);
  if (!matcher) {
    matcher = pantryMatcher(inventory.filter(isRecentDeliveryItem));
    deliveryMatchers.set(inventory, matcher);
  }
  return matcher;
}

/**
 * Calculate delivery bonus for a recipe based on matching recent delivery items.
 * Returns the total bonus score (0 if no delivery matches).
 */
export function calculateDeliveryBonus(
  recipeIngredients: string[],
  inventory: InventoryItem[]
): number {
  if (!recipeIngredients.length || !inventory.length) return 0;

  const delivery = deliveryMatcher(inventory);
  if (delivery.isEmpty) return 0;

  let deliveryMatchCount = 0;
  let perishableMatchCount = 0;

  for (const ingredient of recipeIngredients) {
    const item = delivery.itemsForLine(ingredient)[0];
    if (!item) continue;
    deliveryMatchCount++;
    if (PERISHABLE_CATEGORIES.has(item.category)) {
      perishableMatchCount++;
    }
  }

  if (deliveryMatchCount === 0) return 0;

  let bonus = deliveryMatchCount * DELIVERY_BONUS_PER_MATCH;
  bonus += perishableMatchCount * DELIVERY_PERISHABLE_BONUS;

  if (deliveryMatchCount >= DELIVERY_CLUSTER_THRESHOLD) {
    bonus += DELIVERY_CLUSTER_BONUS;
  }

  return bonus;
}

/**
 * Select the "vedette" (star) ingredient from a recipe's ingredients
 * that matches a recent delivery item. Returns the first (most prominent)
 * delivery ingredient match, or null if none.
 */
export function selectVedetteIngredient(
  recipeIngredients: string[],
  inventory: InventoryItem[]
): string | null {
  if (!recipeIngredients.length || !inventory.length) return null;

  const delivery = deliveryMatcher(inventory);
  if (delivery.isEmpty) return null;

  // Iterate ingredients in order (first = most prominent in recipe)
  for (const ingredient of recipeIngredients) {
    const item = delivery.itemsForLine(ingredient)[0];
    if (item) return item.name;
  }

  return null;
}

/**
 * The recipe's total time, or `null` when we do not have one.
 *
 * `readyInMinutes` is 0 or absent on every recipe whose time we never managed
 * to read off the source, and `|| 30` turned all of them into quick weeknight
 * meals. 0 means unknown, never instant — the same reading
 * `fanSuggestionsService` already takes deliberately.
 */
export function knownCookTime(recipe: { readyInMinutes?: number }): number | null {
  const minutes = Number(recipe.readyInMinutes) || 0;
  return minutes > 0 ? minutes : null;
}

/**
 * Check if a recipe is a quick meal (<=30 minutes or slow cooker)
 */
export function isQuickMeal(recipe: PersonalRecipeIndexEntry, allowPrepAhead = true): boolean {
  // Slow cooker recipes count as quick (hands-off time), until a Préparation says how much it is
  if (!recipe.handsOnMinutes && isSlowCookerRecipe(recipe)) {
    return true;
  }
  // Quick if it takes 30 min or less of the evening (and has a time set)
  const minutes = busyEveningMinutes(recipe, allowPrepAhead);
  return minutes > 0 && minutes <= 30;
}


/**
 * Check if a recipe is pasta-based
 */
export function isPastaRecipe(recipe: PersonalRecipeIndexEntry): boolean {
  const searchText = [
    recipe.title.toLowerCase(),
    ...recipe.ingredients.map(i => i.toLowerCase()),
  ].join(' ');

  // Check for pasta keywords in title/ingredients
  if (/\b(pasta|spaghetti|penne|rigatoni|fusilli|linguine|fettuccine|tagliatelle|gnocchi|lasagna|lasagne|macaroni|mac\s*&?\s*cheese|ravioli|tortellini|orzo|pâtes|carbonara|bolognese|bolognaise|alfredo|puttanesca|cacio e pepe|aglio e olio)\b/i.test(searchText)) {
    return true;
  }

  // Check for Italian cuisine with pasta indicators
  if (recipe.cuisines?.some(c => c.toLowerCase().includes('italian'))) {
    if (/\b(pasta|noodle|sauce)\b/i.test(searchText)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a recipe contains meat (non-fish, non-vegetarian)
 */
export function isMeatRecipe(recipe: PersonalRecipeIndexEntry): boolean {
  const protein = detectProtein(recipe);
  if (!protein) return false;
  const meatProteins = ['chicken', 'beef', 'pork', 'lamb', 'turkey'];
  return meatProteins.includes(protein);
}

/**
 * Detect cuisine from recipe
 */
export function detectCuisine(recipe: PersonalRecipeIndexEntry): string | null {
  // First check the recipe's cuisines array if available
  if (recipe.cuisines && recipe.cuisines.length > 0) {
    const cuisineLower = recipe.cuisines[0].toLowerCase();
    for (const [cuisine, keywords] of Object.entries(CUISINE_KEYWORDS)) {
      if (keywords.some(keyword => cuisineLower.includes(keyword))) {
        return cuisine;
      }
    }
  }

  // Fall back to searching title and ingredients
  const searchText = [
    recipe.title.toLowerCase(),
    ...recipe.ingredients.map(i => i.toLowerCase()),
  ].join(' ');

  for (const [cuisine, keywords] of Object.entries(CUISINE_KEYWORDS)) {
    if (keywords.some(keyword => searchText.includes(keyword))) {
      return cuisine;
    }
  }

  return null;
}

/**
 * Detect dish format from recipe title (e.g., tacos, soup, pasta)
 */
export function detectDishFormat(recipe: { title: string }): string | null {
  const titleLower = recipe.title.toLowerCase();

  for (const [format, keywords] of Object.entries(DISH_FORMAT_KEYWORDS)) {
    if (keywords.some(keyword => titleLower.includes(keyword))) {
      return format;
    }
  }

  return null;
}

/**
 * Detect key flavoring ingredient from recipe title (e.g., coconut, peanut)
 */
export function detectKeyFlavor(recipe: { title: string }): string | null {
  const titleLower = recipe.title.toLowerCase();

  for (const [flavor, keywords] of Object.entries(KEY_FLAVOR_KEYWORDS)) {
    if (keywords.some(keyword => titleLower.includes(keyword))) {
      return flavor;
    }
  }

  return null;
}

/**
 * Score a recipe for a given slot and context
 * Higher score = better fit
 */
export function scoreRecipe(
  recipe: PersonalRecipeIndexEntry,
  slot: WeekSlot,
  context: GenerationContext
): number {
  let score = 100; // Base score

  // === HARD CONSTRAINTS (big penalties) ===

  // Disqualify: nothing to cook or to buy (a blank manual draft, a failed import)
  if (!hasIngredients(recipe)) return -1000;

  // Penalty: Already used this week
  if (context.usedRecipeIds.has(recipe.id)) {
    return -1000; // Disqualify
  }

  // Penalty: Used last week
  if (context.previousWeekRecipeIds.includes(recipe.id)) {
    score -= 200;
  }

  // === VARIETY CONSTRAINTS ===

  // Protein variety (max 3 same protein per week)
  const protein = detectProtein(recipe);
  if (protein) {
    const proteinCount = context.usedProteins.get(protein) || 0;
    if (proteinCount >= 3) {
      score -= 150; // Heavy penalty for too much of same protein
    } else if (proteinCount >= 2) {
      score -= 50;
    }
  }

  // Cuisine variety (max 2 same cuisine per week)
  const cuisine = detectCuisine(recipe);
  if (cuisine) {
    const cuisineCount = context.usedCuisines.get(cuisine) || 0;
    if (cuisineCount >= 2) {
      score -= 100; // Penalty for too much of same cuisine
    } else if (cuisineCount >= 1) {
      score -= 25;
    }
  }

  // Dish format variety (max 1 of same format per week, e.g., no 3 tacos)
  const dishFormat = detectDishFormat(recipe);
  if (dishFormat) {
    const formatCount = context.usedDishFormats.get(dishFormat) || 0;
    if (formatCount >= 2) {
      score -= 120; // Heavy penalty for 3+ of same format
    } else if (formatCount >= 1) {
      score -= 40;
    }
  }

  // Key flavor variety (max 1 of same key flavor per week, e.g., no 3 coconut dishes)
  const keyFlavor = detectKeyFlavor(recipe);
  if (keyFlavor) {
    const flavorCount = context.usedKeyFlavors.get(keyFlavor) || 0;
    if (flavorCount >= 2) {
      score -= 100; // Heavy penalty for 3+ of same key flavor
    } else if (flavorCount >= 1) {
      score -= 35;
    }
  }

  // === COOK TIME CONSTRAINTS ===

  // 0 means nobody knows how long this takes, not "half an hour". Defaulting it
  // to 30 handed a quick-meal bonus to every recipe whose time we never managed
  // to scrape — the same mistake that let a 7-hour gigot win weeknights.
  const cookTime = knownCookTime(recipe);

  if (cookTime !== null) {
    // Lunch (every day, including weekend): always prefer quick & casual meals.
    // The user explicitly does not want long weekend lunches.
    if (slot.mealType === 'lunch') {
      if (cookTime <= 20) {
        score += 30;
      } else if (cookTime <= 30) {
        score += 15;
      } else if (cookTime > 45) {
        score -= 30;
      }
    }

    // Weekday dinner: quick meals preferred (under 30 min)
    if (!slot.isWeekend && slot.mealType === 'dinner') {
      const evening = busyEveningMinutes(recipe);
      if (evening <= 20) {
        score += 30;
      } else if (evening <= 30) {
        score += 20;
      } else if (evening > 45) {
        score -= 20;
      }
    }

    // Weekend dinner only: more time available, bonus for elaborate recipes
    if (slot.isWeekend && slot.mealType === 'dinner') {
      if (cookTime >= 45 && cookTime <= 90) {
        score += 15;
      }
    }
  }

  const dishTypesLower = recipe.dishTypes?.map(dt => dt.toLowerCase()) || [];
  const titleLower = recipe.title.toLowerCase();
  const isBreakfastDish =
    dishTypesLower.some(dt => dt.includes('breakfast')) ||
    BREAKFAST_TITLE_PATTERN.test(titleLower);

  // Breakfast: prefer quick, breakfast-appropriate dishes; actively reject
  // recipes that look like lunch/dinner mains so the breakfast slot doesn't
  // surface things like Poke Bowl or Fettuccine.
  if (slot.mealType === 'breakfast') {
    if (isBreakfastDish) {
      score += 50;
    } else {
      const isMainCourse = dishTypesLower.some(dt =>
        dt.includes('main course') ||
        dt.includes('main dish') ||
        dt.includes('dinner') ||
        dt.includes('lunch') ||
        dt.includes('entree')
      );
      if (isMainCourse) {
        return -1000;
      }
      if (DINNER_TITLE_PATTERN.test(titleLower)) {
        return -1000;
      }
    }

    if (cookTime !== null) {
      if (cookTime <= 20) {
        score += 20;
      } else if (cookTime > 30) {
        score -= 30;
      }
    }
  }

  // Penalty: Breakfast-type dishes should NOT be assigned to lunch/dinner
  if (slot.mealType !== 'breakfast' && isBreakfastDish) {
    score -= 200;
  }

  // === DISH TYPE FILTERING (for lunch/dinner) ===
  // Disqualify or penalize non-main-course dishes for main meals

  if (slot.mealType === 'lunch' || slot.mealType === 'dinner') {
    // DISQUALIFY: Beverages and drinks (not a meal!)
    const isBeverage = dishTypesLower.some(dt =>
      dt.includes('beverage') || dt.includes('drink') || dt.includes('cocktail')
    ) || BEVERAGE_TITLE_RE.test(titleLower);

    if (isBeverage) {
      return -1000; // Disqualify - beverages are not meals
    }

    // DISQUALIFY: Side dishes, appetizers, snacks (not standalone meals)
    const isSideOrSnack = dishTypesLower.some(dt =>
      dt.includes('side dish') ||
      dt.includes('appetizer') ||
      dt.includes('starter') ||
      dt.includes('snack') ||
      dt.includes('condiment') ||
      dt.includes('sauce') ||
      dt.includes('dip')
    ) || titleLower.match(/\b(frozen grapes|popcorn|trail mix|energy balls?|granola bars?|fruit leather|chips and (dip|salsa)|snack mix|rice krispie|cheese straws?|pretzel bites?|edamame|cheese ball|deviled eggs?|fruit salad)\b/i);

    if (isSideOrSnack) {
      return -1000; // Disqualify - snacks/sides are not standalone meals
    }

    // DISQUALIFY: Desserts for main meals (shared detector — same rule as the
    // Remplacer ce plat suggestions)
    if (isDessertRecipe(recipe)) {
      return -1000; // Disqualify - desserts are not main meals
    }

    // BONUS: Main course dishes
    const isMainCourse = dishTypesLower.some(dt =>
      dt.includes('main course') ||
      dt.includes('main dish') ||
      dt.includes('dinner') ||
      dt.includes('lunch') ||
      dt.includes('entree')
    );

    if (isMainCourse) {
      score += 30; // Bonus for proper main course dishes
    }

    // Title-based catch-all: runs regardless of dishTypes to catch misclassified recipes
    const snackTitlePattern = /\b(dip|bites?|balls?|bark|pops?|sticks?|chips?|crackers?|hummus|guacamole|salsa|spread|mix|muffins?|cookies?|brownies?|bars?|smoothies?|shakes?|vinaigrette|dressing|marinade|toast)\b/i;
    if (titleLower.match(snackTitlePattern)) {
      return -1000;
    }

    // When dishTypes is empty, also penalise titles with no dinner-like keywords
    if (dishTypesLower.length === 0) {
      if (!DINNER_TITLE_PATTERN.test(titleLower)) {
        score -= 200; // Penalty - no dinner-like keywords in title
      }
    }
  }

  // === FRESHNESS / USAGE FREQUENCY ===

  // Favor less-frequently-used recipes
  const metadata = context.metadata[recipe.id];
  if (metadata) {
    const timesPlanned = metadata.timesPlanned || 0;

    if (timesPlanned === 0) {
      score += 50; // Never planned before - strongly favor trying new recipes
    } else if (timesPlanned <= 2) {
      score += 20;
    } else if (timesPlanned >= 10) {
      score -= 30;
    }

    // Recency penalty: discourage recipes cooked or planned recently so the
    // same "old favorites" don't keep resurfacing. Prefer lastMade (ground
    // truth) and fall back to lastPlanned when the user hasn't marked it made.
    const recencyTimestamp = metadata.lastMade || metadata.lastPlanned;
    if (recencyTimestamp) {
      const daysSince =
        (Date.now() - new Date(recencyTimestamp).getTime()) / (1000 * 60 * 60 * 24);
      const isMade = !!metadata.lastMade;
      if (daysSince < 14) {
        score -= isMade ? 100 : 60;
      } else if (daysSince < 28) {
        score -= isMade ? 40 : 25;
      }
    }

    // Bonus for highly rated recipes
    if (metadata.rating) {
      score += (metadata.rating - 3) * 10; // +20 for 5-star, +10 for 4-star, etc.
    }
  } else {
    // No metadata = never used, give bonus
    score += 35;
  }

  // === HEALTH SCORE ===
  if (recipe.healthScore) {
    score += Math.min(recipe.healthScore / 10, 10); // Up to +10 for healthy recipes
  }

  // === DEAL BONUS ===
  // Boost recipes that use ingredients from selected deals
  if (context.selectedDeals && context.selectedDeals.length > 0) {
    const matchedDeals = matchRecipeToDeals(recipe, context.selectedDeals);
    if (matchedDeals.length > 0) {
      // Major bonus for using deal ingredients
      score += 50;
      // Extra bonus per matched deal
      score += matchedDeals.length * 15;

      // Extra bonus for protein deals (usually most significant savings)
      const proteinDealMatches = matchedDeals.filter(d => d.category === 'protein').length;
      if (proteinDealMatches > 0) {
        score += proteinDealMatches * 20;
      }
    }
  }

  // === INVENTORY BONUS ===
  // Boost recipes that use ingredients from current inventory
  // Uses flat per-ingredient bonus so even 1 match is meaningful
  if (context.inventory && context.inventory.length > 0) {
    const inventoryMatch = calculateInventoryMatchScore(recipe, context.inventory);
    if (inventoryMatch.matchedCount > 0) {
      // Flat bonus per matched ingredient: +50 each
      score += inventoryMatch.matchedCount * 50;

      // Extra bonus for using expiring ingredients (+40 per expiring item)
      score += inventoryMatch.expiringIngredients.length * 40;
    }
  }

  // === DELIVERY BONUS ===
  // Boost recipes that use ingredients from a recent Lufa delivery
  if (context.inventory && context.inventory.length > 0) {
    const deliveryBonus = calculateDeliveryBonus(
      recipe.ingredients || [],
      context.inventory
    );
    score += deliveryBonus;
  }

  // === RANDOMNESS ===
  // Add small random factor to avoid always picking same recipes
  score += Math.random() * 15;

  return score;
}

/**
 * Generation options including deals
 */
export interface GenerationOptions {
  dietaryConfig?: DietaryConfig;
  selectedDeals?: LufaDeal[];
}

/**
 * Generate a complete weekly meal plan
 * Supports dietary preferences with guaranteed slots for vegetarian and fish meals
 * Optionally boosts recipes using selected deals
 */
export function generateWeeklyPlan(
  weekStartDate: string,
  allRecipes: PersonalRecipeIndexEntry[],
  previousWeekRecipeIds: string[],
  metadata: Record<string, RecipeMetadata>,
  options?: GenerationOptions
): GeneratedMeal[] {
  const slots = generateWeekSlots(weekStartDate);
  const generatedMeals: GeneratedMeal[] = [];
  const dietaryConfig = options?.dietaryConfig;
  const selectedDeals = options?.selectedDeals;

  const context: GenerationContext = {
    previousWeekRecipeIds,
    usedRecipeIds: new Set(),
    usedProteins: new Map(),
    usedCuisines: new Map(),
    usedDishFormats: new Map(),
    usedKeyFlavors: new Map(),
    metadata,
    selectedDeals,
  };

  if (selectedDeals && selectedDeals.length > 0) {
    console.log(`WeekGenerator - Using ${selectedDeals.length} deals for scoring boost`);
  }

  // Track which slots have been reserved for dietary requirements
  const reservedSlots = new Map<string, DietaryRole>(); // slotKey -> dietaryRole

  // === PHASE 1: Reserve slots for dietary requirements ===

  if (dietaryConfig?.includeVegetarian) {
    // Find vegetarian recipes
    const vegetarianRecipes = allRecipes.filter(r => isVegetarianRecipe(r));

    if (vegetarianRecipes.length > 0) {
      // Pick a mid-week dinner slot for vegetarian (Wednesday dinner preferred)
      const dinnerSlots = slots.filter(s => s.mealType === 'dinner' && !s.isWeekend);
      // Try Wednesday first (dayOfWeek 3), then other weekdays
      const wednesdaySlot = dinnerSlots.find(s => s.dayOfWeek === 3);
      const vegetarianSlot = wednesdaySlot || dinnerSlots[Math.floor(dinnerSlots.length / 2)];

      if (vegetarianSlot) {
        const slotKey = `${vegetarianSlot.date}-${vegetarianSlot.mealType}`;

        // Score vegetarian recipes for this slot
        const scoredVegRecipes = vegetarianRecipes
          .map(recipe => ({
            recipe,
            score: scoreRecipe(recipe, vegetarianSlot, context),
          }))
          .filter(r => r.score > -500)
          .sort((a, b) => b.score - a.score);

        if (scoredVegRecipes.length > 0) {
          const selected = scoredVegRecipes[0];

          // Update context
          context.usedRecipeIds.add(selected.recipe.id);
          const protein = detectProtein(selected.recipe);
          if (protein) {
            context.usedProteins.set(protein, (context.usedProteins.get(protein) || 0) + 1);
          }
          const cuisine = detectCuisine(selected.recipe);
          if (cuisine) {
            context.usedCuisines.set(cuisine, (context.usedCuisines.get(cuisine) || 0) + 1);
          }
          const vegDishFormat = detectDishFormat(selected.recipe);
          if (vegDishFormat) {
            context.usedDishFormats.set(vegDishFormat, (context.usedDishFormats.get(vegDishFormat) || 0) + 1);
          }
          const vegKeyFlavor = detectKeyFlavor(selected.recipe);
          if (vegKeyFlavor) {
            context.usedKeyFlavors.set(vegKeyFlavor, (context.usedKeyFlavors.get(vegKeyFlavor) || 0) + 1);
          }

          // Get matched deals for this recipe
          const vegMatchedDeals = selectedDeals
            ? matchRecipeToDeals(selected.recipe, selectedDeals)
            : undefined;

          generatedMeals.push({
            slot: vegetarianSlot,
            recipe: selected.recipe,
            score: selected.score,
            dietaryRole: 'vegetarian',
            matchedDeals: vegMatchedDeals?.length ? vegMatchedDeals : undefined,
          });

          reservedSlots.set(slotKey, 'vegetarian');
          console.log(`WeekGenerator - Reserved ${slotKey} for vegetarian: ${selected.recipe.title}`);
        }
      }
    } else {
      console.warn('WeekGenerator - No vegetarian recipes available, skipping vegetarian requirement');
    }
  }

  if (dietaryConfig?.includeFish) {
    // Find fish/seafood recipes
    const fishRecipes = allRecipes.filter(r => isFishRecipe(r));

    if (fishRecipes.length > 0) {
      // Pick a different slot for fish (Friday dinner preferred - traditional fish Friday)
      const dinnerSlots = slots.filter(s => s.mealType === 'dinner');
      // Try Friday first (dayOfWeek 5), then other days
      const fridaySlot = dinnerSlots.find(s => s.dayOfWeek === 5);
      // Make sure we don't pick the same slot as vegetarian
      let fishSlot = fridaySlot;
      if (fishSlot && reservedSlots.has(`${fishSlot.date}-${fishSlot.mealType}`)) {
        // Friday is taken, pick another dinner slot
        fishSlot = dinnerSlots.find(s =>
          !reservedSlots.has(`${s.date}-${s.mealType}`)
        );
      }
      if (!fishSlot) {
        fishSlot = dinnerSlots.find(s =>
          !reservedSlots.has(`${s.date}-${s.mealType}`)
        );
      }

      if (fishSlot) {
        const slotKey = `${fishSlot.date}-${fishSlot.mealType}`;

        // Score fish recipes for this slot
        const scoredFishRecipes = fishRecipes
          .map(recipe => ({
            recipe,
            score: scoreRecipe(recipe, fishSlot!, context),
          }))
          .filter(r => r.score > -500)
          .sort((a, b) => b.score - a.score);

        if (scoredFishRecipes.length > 0) {
          const selected = scoredFishRecipes[0];

          // Update context
          context.usedRecipeIds.add(selected.recipe.id);
          const protein = detectProtein(selected.recipe);
          if (protein) {
            context.usedProteins.set(protein, (context.usedProteins.get(protein) || 0) + 1);
          }
          const cuisine = detectCuisine(selected.recipe);
          if (cuisine) {
            context.usedCuisines.set(cuisine, (context.usedCuisines.get(cuisine) || 0) + 1);
          }
          const fishDishFormat = detectDishFormat(selected.recipe);
          if (fishDishFormat) {
            context.usedDishFormats.set(fishDishFormat, (context.usedDishFormats.get(fishDishFormat) || 0) + 1);
          }
          const fishKeyFlavor = detectKeyFlavor(selected.recipe);
          if (fishKeyFlavor) {
            context.usedKeyFlavors.set(fishKeyFlavor, (context.usedKeyFlavors.get(fishKeyFlavor) || 0) + 1);
          }

          // Get matched deals for this recipe
          const fishMatchedDeals = selectedDeals
            ? matchRecipeToDeals(selected.recipe, selectedDeals)
            : undefined;

          generatedMeals.push({
            slot: fishSlot,
            recipe: selected.recipe,
            score: selected.score,
            dietaryRole: 'fish',
            matchedDeals: fishMatchedDeals?.length ? fishMatchedDeals : undefined,
          });

          reservedSlots.set(slotKey, 'fish');
          console.log(`WeekGenerator - Reserved ${slotKey} for fish: ${selected.recipe.title}`);
        }
      }
    } else {
      console.warn('WeekGenerator - No fish recipes available, skipping fish requirement');
    }
  }

  // === PHASE 2: Fill remaining slots normally ===

  for (const slot of slots) {
    const slotKey = `${slot.date}-${slot.mealType}`;

    // Skip if this slot was already reserved for a dietary requirement
    if (reservedSlots.has(slotKey)) {
      continue;
    }

    // Score all recipes for this slot
    const scoredRecipes = allRecipes
      .map(recipe => ({
        recipe,
        score: scoreRecipe(recipe, slot, context),
      }))
      .filter(r => r.score > -500) // Filter out disqualified recipes
      .sort((a, b) => b.score - a.score);

    if (scoredRecipes.length === 0) {
      console.warn(`No suitable recipe found for ${slot.date} ${slot.mealType}`);
      continue;
    }

    // Pick the best recipe
    const selected = scoredRecipes[0];

    // Update context
    context.usedRecipeIds.add(selected.recipe.id);

    const protein = detectProtein(selected.recipe);
    if (protein) {
      context.usedProteins.set(protein, (context.usedProteins.get(protein) || 0) + 1);
    }

    const cuisine = detectCuisine(selected.recipe);
    if (cuisine) {
      context.usedCuisines.set(cuisine, (context.usedCuisines.get(cuisine) || 0) + 1);
    }
    const slotDishFormat = detectDishFormat(selected.recipe);
    if (slotDishFormat) {
      context.usedDishFormats.set(slotDishFormat, (context.usedDishFormats.get(slotDishFormat) || 0) + 1);
    }
    const slotKeyFlavor = detectKeyFlavor(selected.recipe);
    if (slotKeyFlavor) {
      context.usedKeyFlavors.set(slotKeyFlavor, (context.usedKeyFlavors.get(slotKeyFlavor) || 0) + 1);
    }

    // Get matched deals for this recipe
    const matchedDeals = selectedDeals
      ? matchRecipeToDeals(selected.recipe, selectedDeals)
      : undefined;

    generatedMeals.push({
      slot,
      recipe: selected.recipe,
      score: selected.score,
      matchedDeals: matchedDeals?.length ? matchedDeals : undefined,
    });
  }

  // Sort meals by date and meal type
  generatedMeals.sort((a, b) => {
    if (a.slot.date !== b.slot.date) {
      return a.slot.date.localeCompare(b.slot.date);
    }
    const mealTypeOrder = { breakfast: 0, lunch: 1, dinner: 2 };
    return mealTypeOrder[a.slot.mealType] - mealTypeOrder[b.slot.mealType];
  });

  console.log(`WeekGenerator - Generated ${generatedMeals.length} meals for week starting ${weekStartDate}`);
  console.log(`  Proteins used:`, Object.fromEntries(context.usedProteins));
  console.log(`  Cuisines used:`, Object.fromEntries(context.usedCuisines));
  if (reservedSlots.size > 0) {
    console.log(`  Dietary reservations:`, Object.fromEntries(reservedSlots));
  }
  if (selectedDeals && selectedDeals.length > 0) {
    const mealsWithDeals = generatedMeals.filter(m => m.matchedDeals && m.matchedDeals.length > 0);
    console.log(`  Meals using deals: ${mealsWithDeals.length}/${generatedMeals.length}`);
  }

  return generatedMeals;
}

// ================================================================================
// DINNER-ONLY MVP GENERATION
// ================================================================================

/**
 * Generate 7 dinner slots for the MVP plan (Mon-Sun, dinner only)
 *
 * Slot assignment:
 * - Monday: Quick dinner
 * - Tuesday: Quick dinner
 * - Wednesday: Vegetarian (reserved) + Quick constraint
 * - Thursday: Quick dinner
 * - Friday: Fish (reserved) - no time constraint (weekend-adjacent)
 * - Saturday: Weekend dinner (no constraints)
 * - Sunday: Weekend dinner (no constraints)
 */
export function generateDinnerOnlySlots(weekStartDate: string, schedule?: MealSchedulePreferences): DinnerSlot[] {
  const slots: DinnerSlot[] = [];
  const effectiveSchedule = schedule || DEFAULT_MEAL_SCHEDULE;

  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const dateStr = addDays(weekStartDate, dayOffset);
    const dayOfWeek = parseIsoDate(dateStr).getDay();
    const dayName = DAY_NAMES[dayOfWeek];

    // Get user's preference for this day, falling back to defaults
    const dayPref = effectiveSchedule.days[dayOfWeek] || DEFAULT_DAY_PREFERENCE;

    // Map MealDietType to DinnerSlotType
    const slotType = mapMealDietToSlotType(dayPref, dayOfWeek);

    slots.push({
      date: dateStr,
      dayOfWeek,
      dayName,
      slotType,
      maxCookTime: dayPref.maxCookTimeMinutes || undefined,
      allowPrepAhead: dayPref.allowPrepAhead,
      kidsPresent: dayPref.kidsPresent,
    });
  }

  // Sort by date (Monday first, then rest of week)
  slots.sort((a, b) => a.date.localeCompare(b.date));

  return slots;
}

/**
 * Map a user's MealDietType preference to a DinnerSlotType
 */
function getConstraintLabel(slot: DinnerSlot): string | undefined {
  switch (slot.slotType) {
    case 'vegetarian': return 'Vegetarian';
    case 'fish': return 'Fish';
    case 'pasta': return 'Pasta';
    case 'meat': return 'Meat';
    case 'quick': return 'Quick meal';
    default: return undefined;
  }
}

function mapMealDietToSlotType(pref: DayPreference, dayOfWeek: number): DinnerSlotType {
  switch (pref.mealType) {
    case 'vegetarian': return 'vegetarian';
    case 'fish': return 'fish';
    case 'pasta': return 'pasta';
    case 'meat': return 'meat';
    case 'any':
      // For 'any', determine based on time constraint and weekend
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return 'weekend';
      }
      if (pref.maxCookTimeMinutes && pref.maxCookTimeMinutes <= 30) {
        return 'quick';
      }
      return 'any';
    default:
      return 'any';
  }
}

/**
 * Generate badges for a dinner
 */
function generateDinnerBadges(recipe: PersonalRecipeIndexEntry): DinnerBadge[] {
  const badges: DinnerBadge[] = [];

  if (isFishRecipe(recipe)) {
    badges.push('fish');
  }

  if (isVegetarianRecipe(recipe)) {
    badges.push('veggie');
  }

  if (isSlowCookerRecipe(recipe)) {
    badges.push('slowcooker');
  } else if (recipe.readyInMinutes > 0 && recipe.readyInMinutes <= 30) {
    badges.push('quick');
  }

  return badges;
}

/**
 * Pick a recipe from scored candidates using weighted random selection.
 * Uses a dynamic pool based on score proximity to the top score,
 * clamped between 15 and 30 candidates. Weights are softened using
 * square root to prevent high-inventory recipes from dominating.
 */
export function weightedRandomPick(scoredRecipes: { recipe: PersonalRecipeIndexEntry; score: number }[]): { recipe: PersonalRecipeIndexEntry; score: number } {
  if (scoredRecipes.length === 0) {
    return scoredRecipes[0]; // Will be undefined, caller should check
  }

  if (scoredRecipes.length === 1) {
    return scoredRecipes[0];
  }

  // Dynamic pool: all recipes within 150 points of top score, clamped 15-30
  const topScore = scoredRecipes[0].score;
  const proximityThreshold = 150;
  let poolSize = scoredRecipes.filter(r => topScore - r.score <= proximityThreshold).length;
  poolSize = Math.max(Math.min(poolSize, 30), Math.min(15, scoredRecipes.length));
  const topN = scoredRecipes.slice(0, poolSize);

  // Shift scores to be positive (min becomes 1) and use sqrt to soften weights.
  // Without sqrt: a 250-point gap → 251x more likely (top recipe always wins).
  // With sqrt: a 250-point gap → ~16x more likely (top recipe favored but others viable).
  const minScore = Math.min(...topN.map(r => r.score));
  const weights = topN.map(r => Math.max(1, Math.sqrt(r.score - minScore + 1)));
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);

  // Weighted random selection
  let random = Math.random() * totalWeight;
  for (let i = 0; i < topN.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return topN[i];
    }
  }

  return topN[0]; // Fallback
}

/**
 * Score a recipe for a dinner-only slot (simpler than full week scoring)
 */
export function scoreDinnerRecipe(
  recipe: PersonalRecipeIndexEntry,
  slot: DinnerSlot,
  usedRecipeIds: Set<string>,
  excludedRecipeIds: string[],
  metadata: Record<string, RecipeMetadata>,
  inventory?: InventoryItem[],
  usedCuisinesThisWeek?: Set<string>,
  deals?: LufaDeal[],
  recentRecipeHistory?: Record<string, number>,
  previouslyGeneratedRecipeIds?: string[],
  tasteProfile?: TasteProfile | null,
  usedDishFormatsThisWeek?: Map<string, number>,
  usedKeyFlavorsThisWeek?: Map<string, number>,
): number {
  let score = 100; // Base score

  // === HARD CONSTRAINTS (disqualify) ===

  // Disqualify: nothing to cook or to buy (a blank manual draft, a failed import)
  if (!hasIngredients(recipe)) return -1000;

  // Disqualify: Already used this week
  if (usedRecipeIds.has(recipe.id)) {
    return -1000;
  }

  // Disqualify: In previouslyGeneratedRecipeIds (re-generation exclusion)
  if (previouslyGeneratedRecipeIds && previouslyGeneratedRecipeIds.includes(recipe.id)) {
    return -1000;
  }

  // Graduated history penalties (replaces single-week exclusion when history available)
  if (recentRecipeHistory && recentRecipeHistory[recipe.id] !== undefined) {
    const weeksAgo = recentRecipeHistory[recipe.id];
    if (weeksAgo === 1) {
      return -1000; // Hard disqualify (same as before)
    } else if (weeksAgo === 2) {
      score -= 500;
    } else if (weeksAgo === 3) {
      score -= 200;
    }
    // 4+ weeks ago: no penalty
  } else if (excludedRecipeIds.includes(recipe.id)) {
    // Fallback: use old excludedRecipeIds for backward compat
    return -1000;
  }

  // Disqualify: Beverages and drinks
  const dishTypesLower = recipe.dishTypes?.map(dt => dt.toLowerCase()) || [];
  const titleLower = recipe.title.toLowerCase();

  const isBeverage = dishTypesLower.some(dt =>
    dt.includes('beverage') || dt.includes('drink') || dt.includes('cocktail')
  ) || BEVERAGE_TITLE_RE.test(titleLower);

  if (isBeverage) {
    return -1000;
  }

  // Disqualify: Breakfast dishes
  const isBreakfastDish = dishTypesLower.some(dt =>
    dt.includes('breakfast')
  ) || titleLower.match(/\b(pancake|waffle|omelette|eggs|toast|muffin|granola|porridge|acai|cereal|oatmeal|breakfast|brioche|crêpes|pain doré)\b/i);

  if (isBreakfastDish) {
    return -1000;
  }

  // Disqualify: Desserts, cookies, and baked sweets (shared detector)
  if (isDessertRecipe(recipe)) {
    return -1000;
  }

  // Disqualify: Snacks, side dishes, appetizers, condiments
  const isSideOrSnack = dishTypesLower.some(dt =>
    dt.includes('side dish') ||
    dt.includes('appetizer') ||
    dt.includes('starter') ||
    dt.includes('snack') ||
    dt.includes('condiment') ||
    dt.includes('sauce') ||
    dt.includes('dip')
  ) || titleLower.match(/\b(frozen grapes|popcorn|trail mix|energy balls?|granola bars?|fruit leather|chips and (dip|salsa)|snack mix|rice krispie|cheese straws?|pretzel bites?|edamame|cheese ball|deviled eggs?|fruit salad|vinaigrette|dressing|marinade)\b/i);

  if (isSideOrSnack) {
    return -1000;
  }

  // Title-based catch-all: runs regardless of dishTypes to catch misclassified recipes
  const snackTitlePattern = /\b(dip|bites?|balls?|bark|pops?|sticks?|chips?|crackers?|hummus|guacamole|salsa|spread|mix|muffins?|cookies?|brownies?|bars?|smoothies?|shakes?|vinaigrette|dressing|marinade|toast)\b/i;
  if (titleLower.match(snackTitlePattern)) {
    return -1000;
  }

  // When dishTypes is empty, also penalise titles with no dinner-like keywords
  if (dishTypesLower.length === 0) {
    const dinnerTitlePattern = /\b(chicken|beef|salmon|pork|lamb|shrimp|fish|tofu|pasta|stew|curry|roast|bake[ds]?|casserole|soup|rice|noodles?|stir.?fry|lasagna|risotto|chili|tacos?|burrito|enchilada|burger|meat|sausage|turkey|duck|veal|lobster|crab|scallop|filet|fillet|gratin|braise[ds]?|ragout|ragu|bolognese|carbonara|primavera|alfredo|pot pie|shepherd|stroganoff|goulash|tagine|biryani|tikka|masala|vindaloo|korma|teriyaki|katsu|ramen|pho|pad thai|fried rice|lo mein|chow mein|jambalaya|gumbo|paella|coq au vin|bourguignon|blanquette|pot-au-feu|poulet|boeuf|porc|agneau|saumon|crevettes|poisson)\b/i;
    if (!titleLower.match(dinnerTitlePattern)) {
      score -= 200; // Penalty - no dinner-like keywords in title
    }
  }

  // === SLOT TYPE CONSTRAINTS ===

  // null = unknown. An unknown time must not earn the ≤20 min bonus, count as
  // an elaborate weekend dish, or trip the max-cook-time penalty.
  const cookTime = knownCookTime(recipe);
  // The cap and the quick slot judge the cook's own time (#850); the weekend's bonus keeps reading Prêt en.
  const eveningTime = knownCookTime({ readyInMinutes: busyEveningMinutes(recipe, slot.allowPrepAhead) });
  const canBypassTimeConstraint = escapesTimeCap(recipe, slot.allowPrepAhead);

  switch (slot.slotType) {
    case 'quick':
      // Must be quick or prep-ahead if allowed
      if (!isQuickMeal(recipe, slot.allowPrepAhead) && !canBypassTimeConstraint) {
        return -499; // Heavy penalty but not disqualified (> -500 threshold)
      }
      if (canBypassTimeConstraint && !isQuickMeal(recipe, slot.allowPrepAhead)) {
        score += 10; // Small bonus for prep-ahead fitting the slot
      }
      // Bonus for very quick meals
      if (eveningTime !== null && eveningTime <= 20) {
        score += 30;
      }
      break;

    case 'vegetarian':
      // Must be vegetarian
      if (!isVegetarianRecipe(recipe)) {
        return -1000; // Disqualify non-vegetarian
      }
      // Apply time constraint (with prep-ahead exception)
      if (slot.maxCookTime && !isQuickMeal(recipe, slot.allowPrepAhead) && !canBypassTimeConstraint) {
        score -= 50;
      }
      score += 20; // Bonus for fitting the slot
      break;

    case 'fish':
      // Must be fish/seafood
      if (!isFishRecipe(recipe)) {
        return -1000; // Disqualify non-fish
      }
      score += 20; // Bonus for fitting the slot
      break;

    case 'pasta':
      // Strongly prefer pasta recipes
      if (isPastaRecipe(recipe)) {
        score += 40; // Strong bonus for pasta
      } else {
        return -1000; // Disqualify non-pasta
      }
      break;

    case 'meat':
      // Must contain meat
      if (!isMeatRecipe(recipe)) {
        return -1000; // Disqualify non-meat
      }
      score += 20; // Bonus for fitting the slot
      break;

    case 'weekend':
      // No constraints, but bonus for more elaborate recipes
      if (cookTime !== null && cookTime >= 45 && cookTime <= 90) {
        score += 15;
      }
      break;

    case 'any':
      // No dietary constraint, just apply time constraint if present
      if (slot.maxCookTime && !isQuickMeal(recipe, slot.allowPrepAhead) && !canBypassTimeConstraint) {
        score -= 50;
      }
      break;
  }

  // Apply time constraint for all slot types (if set and not bypassed)
  if (slot.maxCookTime && eveningTime !== null && eveningTime > slot.maxCookTime && !canBypassTimeConstraint) {
    score -= 30; // Additional penalty for exceeding time limit
  }

  // === DISH FORMAT & KEY FLAVOR VARIETY ===

  if (usedDishFormatsThisWeek) {
    const dishFormat = detectDishFormat(recipe);
    if (dishFormat) {
      const formatCount = usedDishFormatsThisWeek.get(dishFormat) || 0;
      if (formatCount >= 2) {
        score -= 120;
      } else if (formatCount >= 1) {
        score -= 40;
      }
    }
  }

  if (usedKeyFlavorsThisWeek) {
    const keyFlavor = detectKeyFlavor(recipe);
    if (keyFlavor) {
      const flavorCount = usedKeyFlavorsThisWeek.get(keyFlavor) || 0;
      if (flavorCount >= 2) {
        score -= 100;
      } else if (flavorCount >= 1) {
        score -= 35;
      }
    }
  }

  // === BONUS for main course ===
  const isMainCourse = dishTypesLower.some(dt =>
    dt.includes('main course') ||
    dt.includes('main dish') ||
    dt.includes('dinner') ||
    dt.includes('entree')
  );

  if (isMainCourse) {
    score += 30;
  }

  // === EXCITEMENT SCORE ===
  // Combines rating, novelty, favorites, and cuisine variety
  const recipeMetadata = metadata[recipe.id];
  const excitementContext: ExcitementContext = {
    usedCuisinesThisWeek: usedCuisinesThisWeek || new Set(),
    tasteProfile: tasteProfile,
  };
  score += calculateExcitementScore(recipe, recipeMetadata, excitementContext);

  // === INVENTORY BONUS ===
  // Boost recipes that use ingredients from current inventory.
  // Capped at +200 total so inventory is meaningful but doesn't dominate variety.
  if (inventory && inventory.length > 0) {
    const inventoryMatch = calculateInventoryMatchScore(recipe, inventory);
    if (inventoryMatch.matchedCount > 0) {
      // +40 per matched ingredient, +30 per expiring, capped at 200
      let inventoryBonus = inventoryMatch.matchedCount * 40;
      inventoryBonus += inventoryMatch.expiringIngredients.length * 30;
      score += Math.min(inventoryBonus, 200);
    }
  }

  // === DEAL BONUS ===
  if (deals && deals.length > 0) {
    const matchedDeals = matchRecipeToDeals(recipe, deals);
    if (matchedDeals.length > 0) {
      const proteinDealCount = matchedDeals.filter(d => d.category === 'protein').length;
      score += calculateDealBonus(matchedDeals.length, proteinDealCount);

      // Freshness bonus: perishable deals get time-decay bonus
      for (const deal of matchedDeals) {
        score += calculateFreshnessBonus(slot.dayOfWeek, deal.category);
      }
    }
  }

  // === RECENCY BONUS ===
  // Favor recently added recipes (within last 14 days get a boost)
  if (recipeMetadata?.firstSeenAt) {
    const daysSinceAdded = (Date.now() - new Date(recipeMetadata.firstSeenAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceAdded <= 7) {
      score += 60; // Strong boost for recipes added in the last week
    } else if (daysSinceAdded <= 14) {
      score += 30; // Moderate boost for recipes added in the last 2 weeks
    }
  }

  // === COVERAGE BONUS ===
  // Mildly prefer recipes user can make with what they already have
  if (inventory || deals) {
    const coverage = classifyIngredients(recipe, [], inventory || []);
    if (coverage.coveragePercent >= 90) score += 20;
    else if (coverage.coveragePercent >= 70) score += 10;
  }

  // === RANDOMNESS for variety ===
  score += Math.random() * 50;

  return score;
}

/**
 * Generate a 7-dinner MVP meal plan
 *
 * This is a simplified version of generateWeeklyPlan for the MVP that:
 * - Only generates dinners (not breakfast/lunch)
 * - Has fixed slot types (Mon-Thu quick, Wed vegetarian, Fri fish, Sat-Sun weekend)
 * - Respects exclusion list from confirmed previous week
 */
export function generateDinnerOnlyPlan(
  weekStartDate: string,
  allRecipes: PersonalRecipeIndexEntry[],
  config: DinnerMVPConfig,
  metadata: Record<string, RecipeMetadata>
): GeneratedDinner[] {
  let filteredRecipes = allRecipes;

  if (config.dietaryRestrictions && config.dietaryRestrictions.length > 0) {
    filteredRecipes = filteredRecipes.filter(recipe =>
      satisfiesDietaryRestrictions(recipe, config.dietaryRestrictions!));
    console.log(`DinnerMVP - Dietary filter [${config.dietaryRestrictions.join(', ')}]: ${allRecipes.length} → ${filteredRecipes.length} recipes`);
  }

  // Allergens and disliked ingredients share one word-anchored matcher, so
  // "lait" no longer removes every "laitue" and "oeuf" leaves "boeuf" alone.
  const excludedTerms = [
    ...(config.allergenTerms ?? []),
    ...(config.dislikedIngredients ?? []).map(normalizeAllergenText).filter(Boolean),
  ];
  if (excludedTerms.length > 0) {
    filteredRecipes = filteredRecipes.filter(recipe => !recipeHasAllergen(recipe, excludedTerms));
    console.log(`DinnerMVP - Ingredient exclusion filter: ${excludedTerms.length} terms → ${filteredRecipes.length} recipes`);
  }

  const allSlots = generateDinnerOnlySlots(weekStartDate, config.mealSchedule);
  const generatedDinners: GeneratedDinner[] = [];
  const usedRecipeIds = new Set<string>();
  const usedCuisinesThisWeek = new Set<string>();
  const usedDishFormatsThisWeek = new Map<string, number>();
  const usedKeyFlavorsThisWeek = new Map<string, number>();

  // Filter out past days only when todayOverride is set (i.e. generating for the current week)
  // For future weeks, generate all 7 slots
  const slots = allSlots.filter(s =>
    (!config.todayOverride || s.date >= config.todayOverride) &&
    (!config.untilDate || s.date <= config.untilDate));

  // The same meal twice in two weeks is out, whatever the scores say — a
  // hard rule, not one more penalty for a good score to outweigh (#736).
  const sameMeals = new SameMealLedger(config.sameMealHistory);

  // Promote unconstrained slots to hit user's weekly frequency targets
  if (config.mealFrequencyPreferences) {
    const freq = config.mealFrequencyPreferences;
    const currentVeg = slots.filter(s => s.slotType === 'vegetarian').length;
    const currentFish = slots.filter(s => s.slotType === 'fish').length;
    let vegNeeded = Math.max(0, (freq.vegetarianPerWeek ?? 0) - currentVeg);
    let fishNeeded = Math.max(0, (freq.fishPerWeek ?? 0) - currentFish);
    for (const slot of slots) {
      if (vegNeeded <= 0 && fishNeeded <= 0) break;
      if (slot.slotType !== 'any' && slot.slotType !== 'quick' && slot.slotType !== 'weekend') continue;
      if (vegNeeded > 0) { slot.slotType = 'vegetarian'; vegNeeded--; }
      else if (fishNeeded > 0) { slot.slotType = 'fish'; fishNeeded--; }
    }
  }

  // A balanced week: no third dinner on one protein, no third fish dinner
  // unless the schedule asks for it — shared with every generator (planRanking).
  const balance = new WeekBalance(slots.filter(s => s.slotType === 'fish').length);

  console.log(`DinnerMVP - Generating dinners for week starting ${weekStartDate} (${slots.length} slots from today onward)`);
  console.log(`DinnerMVP - Excluding ${config.excludedRecipeIds.length} recipes from last week`);
  if (config.inventory && config.inventory.length > 0) {
    console.log(`DinnerMVP - Inventory has ${config.inventory.length} items: ${config.inventory.map(i => i.name).join(', ')}`);
  } else {
    console.log(`DinnerMVP - No inventory items provided`);
  }

  // Process slots in order: fish/vegetarian first (constrained), then rest
  const constrainedTypes: DinnerSlotType[] = ['fish', 'vegetarian', 'pasta', 'meat'];
  const constrainedSlots = slots.filter(s => constrainedTypes.includes(s.slotType));
  const otherSlots = slots.filter(s => !constrainedTypes.includes(s.slotType));

  // Fill constrained slots first
  for (const slot of [...constrainedSlots, ...otherSlots]) {
    // Score all recipes for this slot (including deal-optimized candidates)
    const candidateRecipes = config.deals && config.deals.length > 0
      ? deduplicateRecipes(filteredRecipes, findDealOptimizedRecipes(filteredRecipes, config.deals))
      : filteredRecipes;

    const scored = candidateRecipes
      .map(recipe => ({
        recipe,
        score: scoreDinnerRecipe(recipe, slot, usedRecipeIds, config.excludedRecipeIds, metadata, config.inventory, usedCuisinesThisWeek, config.deals, config.recentRecipeHistory, config.previouslyGeneratedRecipeIds, config.tasteProfile, usedDishFormatsThisWeek, usedKeyFlavorsThisWeek)
          + (config.tasteAxes ? tasteAxesBonus(recipe, config.tasteAxes, slot.dayOfWeek === 0 || slot.dayOfWeek === 6) : 0),
      }))
      .filter(r => r.score > -500)
      .sort((a, b) => b.score - a.score);

    const { kept: allowed, tier } = sameMeals.allowed(scored, r => mealIdentityOf(r.recipe), slot.date);
    // The slot type already holds the kind; this holds the time cap and the
    // week's balance, each giving way before the slot is left empty.
    const planSlot: PlanSlot = {
      date: slot.date,
      dayOfWeek: slot.dayOfWeek,
      kind: slot.slotType === 'fish' || slot.slotType === 'vegetarian' || slot.slotType === 'meat' || slot.slotType === 'pasta'
        ? slot.slotType
        : 'any',
      maxCookTime: slot.maxCookTime ?? null,
      allowPrepAhead: !!slot.allowPrepAhead,
    };
    const scoredRecipes = narrowForSlot(allowed, r => r.recipe, planSlot, balance);
    if (tier > 0 && scoredRecipes.length > 0) {
      console.warn(`DinnerMVP - ${slot.dayName}: relaxed the same-meal rules to tier ${tier} to fill the slot`);
    }

    if (scoredRecipes.length === 0) {
      console.warn(`DinnerMVP - No suitable recipe for ${slot.dayName} (${slot.slotType})`);
      continue;
    }

    // Log score distribution for debugging variety
    const top5 = scoredRecipes.slice(0, 5).map(r => `${r.recipe.title.substring(0, 25)}(${Math.round(r.score)})`);
    const poolWithin150 = scoredRecipes.filter(r => scoredRecipes[0].score - r.score <= 150).length;
    console.log(`DinnerMVP - ${slot.dayName} pool: ${scoredRecipes.length} eligible, ${poolWithin150} within 150pts, top5: ${top5.join(', ')}`);

    // Pick randomly from top candidates (weighted by score) for variety
    const selected = weightedRandomPick(scoredRecipes);
    usedRecipeIds.add(selected.recipe.id);
    sameMeals.add(slot.date, mealIdentityOf(selected.recipe));
    balance.add(selected.recipe, planSlot);

    // Track cuisine, dish format, and key flavor for variety scoring
    const selectedCuisine = detectCuisine(selected.recipe);
    if (selectedCuisine) {
      usedCuisinesThisWeek.add(selectedCuisine);
    }
    const selectedFormat = detectDishFormat(selected.recipe);
    if (selectedFormat) {
      usedDishFormatsThisWeek.set(selectedFormat, (usedDishFormatsThisWeek.get(selectedFormat) || 0) + 1);
    }
    const selectedFlavor = detectKeyFlavor(selected.recipe);
    if (selectedFlavor) {
      usedKeyFlavorsThisWeek.set(selectedFlavor, (usedKeyFlavorsThisWeek.get(selectedFlavor) || 0) + 1);
    }

    // Build match metadata
    const recipeMetadata = metadata[selected.recipe.id];
    const matchMeta: MatchMetadata = {};

    // Inventory match info
    if (config.inventory && config.inventory.length > 0) {
      const invMatch = calculateInventoryMatchScore(selected.recipe, config.inventory);
      if (invMatch.totalCount > 0) {
        matchMeta.inventoryMatchPercent = Math.round((invMatch.matchedCount / invMatch.totalCount) * 100);
      }
      if (invMatch.expiringIngredients.length > 0) {
        matchMeta.usesExpiringIngredients = invMatch.expiringIngredients;
      }
    }

    // Novelty
    if (!recipeMetadata || recipeMetadata.timesPlanned === 0) {
      matchMeta.isNewRecipe = true;
    }

    // Constraint label
    matchMeta.constraintLabel = getConstraintLabel(slot);

    // Taste profile bonus
    if (config.tasteProfile) {
      const bonus = calculateTasteProfileBonus(selected.recipe, config.tasteProfile);
      if (bonus !== 0) {
        matchMeta.tasteProfileBonus = bonus;
      }
    }

    // Deal matching — find which deals this recipe uses
    let matchedDeals: LufaDeal[] | undefined;
    if (config.deals && config.deals.length > 0) {
      const dealMatches = matchRecipeToDeals(selected.recipe, config.deals);
      if (dealMatches.length > 0) {
        matchedDeals = dealMatches.map(dm => config.deals!.find(d => d.id === dm.id)!).filter(Boolean);
        matchMeta.dealCount = matchedDeals.length;
      }
    }

    // Vedette ingredient — star delivery ingredient for this recipe
    if (config.inventory && config.inventory.length > 0) {
      const vedette = selectVedetteIngredient(
        selected.recipe.ingredients || [],
        config.inventory
      );
      if (vedette) {
        matchMeta.vedetteIngredient = vedette;
      }
    }

    // Ingredient coverage — classify each ingredient as lufa/pantry/toBuy
    const perIngredientDealMatches = config.deals && config.deals.length > 0
      ? matchIngredientsToDeals(selected.recipe.ingredients || [], config.deals)
      : [];
    matchMeta.ingredientCoverage = classifyIngredients(
      selected.recipe,
      perIngredientDealMatches,
      config.inventory || [],
    );

    generatedDinners.push({
      slot,
      recipe: selected.recipe,
      score: selected.score,
      badges: generateDinnerBadges(selected.recipe),
      matchMetadata: matchMeta,
      matchedDeals,
    });

    // Log for debugging
    const invLog = matchMeta.inventoryMatchPercent != null ? `, inv: ${matchMeta.inventoryMatchPercent}%` : '';
    const dealLog = matchedDeals ? `, deals: ${matchedDeals.length}` : '';
    const vedetteLog = matchMeta.vedetteIngredient ? `, vedette: ${matchMeta.vedetteIngredient}` : '';
    console.log(`DinnerMVP - ${slot.dayName}: ${selected.recipe.title} (${slot.slotType}, score: ${Math.round(selected.score)}${invLog}${dealLog}${vedetteLog})`);
  }

  // Sort by date
  generatedDinners.sort((a, b) => a.slot.date.localeCompare(b.slot.date));

  console.log(`DinnerMVP - Generated ${generatedDinners.length} dinners`);

  return generatedDinners;
}

/**
 * Get a replacement suggestion for a dinner slot
 */
export function getDinnerSuggestion(
  slot: DinnerSlot,
  allRecipes: PersonalRecipeIndexEntry[],
  excludeRecipeIds: string[],
  currentWeekRecipeIds: string[],
  confirmedExclusionIds: string[],
  metadata: Record<string, RecipeMetadata>,
  inventory?: InventoryItem[]
): PersonalRecipeIndexEntry | null {
  const usedRecipeIds = new Set([
    ...currentWeekRecipeIds.filter(id => !excludeRecipeIds.includes(id)),
    ...excludeRecipeIds, // Also exclude the one being replaced
  ]);

  const scoredRecipes = allRecipes
    .map(recipe => ({
      recipe,
      score: scoreDinnerRecipe(recipe, slot, usedRecipeIds, confirmedExclusionIds, metadata, inventory, new Set()),
    }))
    .filter(r => r.score > -500)
    .sort((a, b) => b.score - a.score);

  if (scoredRecipes.length === 0) {
    return null;
  }

  return scoredRecipes[0].recipe;
}

// ================================================================================
// END DINNER-ONLY MVP GENERATION
// ================================================================================

/**
 * Get a replacement suggestion for a specific meal slot
 * Used for double-tap replacement feature
 *
 * @param dietaryRole - If the meal being replaced has a dietary role (vegetarian/fish),
 *                      the suggestion will be constrained to match that requirement
 */
export function getSuggestion(
  slot: WeekSlot,
  allRecipes: PersonalRecipeIndexEntry[],
  excludeRecipeIds: string[],
  currentWeekRecipeIds: string[],
  previousWeekRecipeIds: string[],
  metadata: Record<string, RecipeMetadata>,
  dietaryRole?: DietaryRole
): PersonalRecipeIndexEntry | null {
  // Build context with current week's recipes (excluding the one being replaced)
  const usedProteins = new Map<string, number>();
  const usedCuisines = new Map<string, number>();
  const usedDishFormats = new Map<string, number>();
  const usedKeyFlavors = new Map<string, number>();

  for (const recipeId of currentWeekRecipeIds) {
    if (excludeRecipeIds.includes(recipeId)) continue;

    const recipe = allRecipes.find(r => r.id === recipeId);
    if (!recipe) continue;

    const protein = detectProtein(recipe);
    if (protein) {
      usedProteins.set(protein, (usedProteins.get(protein) || 0) + 1);
    }

    const cuisine = detectCuisine(recipe);
    if (cuisine) {
      usedCuisines.set(cuisine, (usedCuisines.get(cuisine) || 0) + 1);
    }

    const format = detectDishFormat(recipe);
    if (format) {
      usedDishFormats.set(format, (usedDishFormats.get(format) || 0) + 1);
    }

    const flavor = detectKeyFlavor(recipe);
    if (flavor) {
      usedKeyFlavors.set(flavor, (usedKeyFlavors.get(flavor) || 0) + 1);
    }
  }

  const context: GenerationContext = {
    previousWeekRecipeIds,
    usedRecipeIds: new Set(currentWeekRecipeIds.filter(id => !excludeRecipeIds.includes(id))),
    usedProteins,
    usedCuisines,
    usedDishFormats,
    usedKeyFlavors,
    metadata,
  };

  // Also exclude the recipes we're replacing
  for (const id of excludeRecipeIds) {
    context.usedRecipeIds.add(id);
  }

  // Filter recipes based on dietary role constraint
  let candidateRecipes = allRecipes.filter(r => !excludeRecipeIds.includes(r.id));

  if (dietaryRole === 'vegetarian') {
    candidateRecipes = candidateRecipes.filter(r => isVegetarianRecipe(r));
    console.log(`getSuggestion - Filtering for vegetarian: ${candidateRecipes.length} candidates`);
  } else if (dietaryRole === 'fish') {
    candidateRecipes = candidateRecipes.filter(r => isFishRecipe(r));
    console.log(`getSuggestion - Filtering for fish: ${candidateRecipes.length} candidates`);
  }

  // Score filtered recipes
  const scoredRecipes = candidateRecipes
    .map(recipe => ({
      recipe,
      score: scoreRecipe(recipe, slot, context),
    }))
    .filter(r => r.score > -500)
    .sort((a, b) => b.score - a.score);

  if (scoredRecipes.length > 0) {
    return scoredRecipes[0].recipe;
  }

  // Fallback: drop dietary filter and dedup, return the best-scoring recipe
  // from the full library so the user always gets something when they tap
  // "New Idea". Better to suggest a repeat than fail silently.
  if (dietaryRole) {
    console.warn(`getSuggestion - No ${dietaryRole} recipes; falling back to full library`);
  }

  const fallbackContext: GenerationContext = {
    ...context,
    usedRecipeIds: new Set(excludeRecipeIds),
    previousWeekRecipeIds: [],
  };
  const fallbackPool = allRecipes.filter(r => !excludeRecipeIds.includes(r.id));
  const fallbackScored = fallbackPool
    .map(recipe => ({ recipe, score: scoreRecipe(recipe, slot, fallbackContext) }))
    .filter(r => r.score > -500)
    .sort((a, b) => b.score - a.score);

  return fallbackScored[0]?.recipe ?? null;
}

/**
 * Badge types for suggestion cards
 */
export type SuggestionBadge =
  | { type: 'quick'; label: string }
  | { type: 'veggie' }
  | { type: 'fish' }
  | { type: 'favorite' }
  | { type: 'new' }
  | { type: 'cuisine'; label: string };

/**
 * Scored suggestion with badges
 */
export interface ScoredSuggestion {
  recipe: PersonalRecipeIndexEntry;
  score: number;
  badges: SuggestionBadge[];
}

/**
 * Result of getTopSuggestions, including whether a relaxed fallback pass was
 * used (e.g. dietary filter dropped, or week-dedup ignored) so the UI can
 * tell the user what they're looking at.
 */
export interface TopSuggestionsResult {
  suggestions: ScoredSuggestion[];
  usedFallback: boolean;
}

/**
 * Context for dietary quota checking
 */
interface DietaryContext {
  needsVegetarian: boolean;
  needsFish: boolean;
}

/**
 * Generate badges for a recipe suggestion
 */
function generateBadges(
  recipe: PersonalRecipeIndexEntry,
  metadata: Record<string, RecipeMetadata>,
  dietaryContext: DietaryContext
): SuggestionBadge[] {
  const badges: SuggestionBadge[] = [];
  const recipeMetadata = metadata[recipe.id];
  const cookTime = knownCookTime(recipe);

  // Quick badge (≤20 min). Never for an unknown time — the badge prints the
  // number, so `|| 30` would have put "30 min" on a recipe nobody has timed.
  if (cookTime !== null && cookTime <= 20) {
    badges.push({ type: 'quick', label: `${cookTime} min` });
  }

  // Veggie badge (if week needs it)
  if (dietaryContext.needsVegetarian && isVegetarianRecipe(recipe)) {
    badges.push({ type: 'veggie' });
  }

  // Fish badge (if week needs it)
  if (dietaryContext.needsFish && isFishRecipe(recipe)) {
    badges.push({ type: 'fish' });
  }

  // Favorite badge
  if (recipeMetadata?.rating && recipeMetadata.rating >= 4) {
    badges.push({ type: 'favorite' });
  }

  // New/try something new badge
  if (!recipeMetadata || (recipeMetadata.timesPlanned || 0) === 0) {
    badges.push({ type: 'new' });
  }

  // Limit to 2 badges max for UI
  return badges.slice(0, 2);
}

/**
 * Greedy re-ranker that picks `count` items from a pre-sorted candidate pool
 * while penalizing repeated protein / dish format / key flavor across already
 * picked items. Prevents the displayed list from being three salmons in a row
 * even when the raw scores would put them on top.
 *
 * The original score is left untouched on each item; only selection order
 * changes.
 */
function selectDiverseSuggestions(
  sortedCandidates: ScoredSuggestion[],
  count: number
): ScoredSuggestion[] {
  const PROTEIN_PENALTY = 60;
  const FORMAT_PENALTY = 50;
  const FLAVOR_PENALTY = 40;
  // Pull from a wider pool than `count` so the re-ranker has room to
  // substitute in diverse alternatives.
  const POOL_SIZE = Math.max(count * 6, 30);

  const pool = sortedCandidates.slice(0, POOL_SIZE);
  const selected: ScoredSuggestion[] = [];
  const usedIndices = new Set<number>();
  const proteinCounts = new Map<string, number>();
  const formatCounts = new Map<string, number>();
  const flavorCounts = new Map<string, number>();

  while (selected.length < count && usedIndices.size < pool.length) {
    let bestIdx = -1;
    let bestAdjusted = -Infinity;

    for (let i = 0; i < pool.length; i++) {
      if (usedIndices.has(i)) continue;

      const candidate = pool[i];
      const protein = detectProtein(candidate.recipe);
      const format = detectDishFormat(candidate.recipe);
      const flavor = detectKeyFlavor(candidate.recipe);

      let penalty = 0;
      if (protein) penalty += PROTEIN_PENALTY * (proteinCounts.get(protein) || 0);
      if (format) penalty += FORMAT_PENALTY * (formatCounts.get(format) || 0);
      if (flavor) penalty += FLAVOR_PENALTY * (flavorCounts.get(flavor) || 0);

      const adjusted = candidate.score - penalty;
      if (adjusted > bestAdjusted) {
        bestAdjusted = adjusted;
        bestIdx = i;
      }
    }

    if (bestIdx === -1) break;

    const picked = pool[bestIdx];
    selected.push(picked);
    usedIndices.add(bestIdx);

    const protein = detectProtein(picked.recipe);
    const format = detectDishFormat(picked.recipe);
    const flavor = detectKeyFlavor(picked.recipe);
    if (protein) proteinCounts.set(protein, (proteinCounts.get(protein) || 0) + 1);
    if (format) formatCounts.set(format, (formatCounts.get(format) || 0) + 1);
    if (flavor) flavorCounts.set(flavor, (flavorCounts.get(flavor) || 0) + 1);
  }

  return selected;
}

/**
 * Get top N recipe suggestions for an empty meal slot
 * Returns scored suggestions with badges explaining why each is recommended
 */
export function getTopSuggestions(
  slot: WeekSlot,
  allRecipes: PersonalRecipeIndexEntry[],
  currentWeekRecipeIds: string[],
  previousWeekRecipeIds: string[],
  metadata: Record<string, RecipeMetadata>,
  dietaryConfig?: DietaryConfig,
  count: number = 5
): TopSuggestionsResult {
  // Build context from current week's recipes
  const usedProteins = new Map<string, number>();
  const usedCuisines = new Map<string, number>();
  const usedDishFormats = new Map<string, number>();
  const usedKeyFlavors = new Map<string, number>();
  let hasVegetarian = false;
  let hasFish = false;

  for (const recipeId of currentWeekRecipeIds) {
    const recipe = allRecipes.find(r => r.id === recipeId);
    if (!recipe) continue;

    const protein = detectProtein(recipe);
    if (protein) {
      usedProteins.set(protein, (usedProteins.get(protein) || 0) + 1);
    }

    const cuisine = detectCuisine(recipe);
    if (cuisine) {
      usedCuisines.set(cuisine, (usedCuisines.get(cuisine) || 0) + 1);
    }

    const format = detectDishFormat(recipe);
    if (format) {
      usedDishFormats.set(format, (usedDishFormats.get(format) || 0) + 1);
    }

    const flavor = detectKeyFlavor(recipe);
    if (flavor) {
      usedKeyFlavors.set(flavor, (usedKeyFlavors.get(flavor) || 0) + 1);
    }

    // Track dietary fulfillment
    if (isVegetarianRecipe(recipe)) hasVegetarian = true;
    if (isFishRecipe(recipe)) hasFish = true;
  }

  const context: GenerationContext = {
    previousWeekRecipeIds,
    usedRecipeIds: new Set(currentWeekRecipeIds),
    usedProteins,
    usedCuisines,
    usedDishFormats,
    usedKeyFlavors,
    metadata,
  };

  // Determine if we need to boost dietary options
  const dietaryContext: DietaryContext = {
    needsVegetarian: (dietaryConfig?.includeVegetarian ?? false) && !hasVegetarian,
    needsFish: (dietaryConfig?.includeFish ?? false) && !hasFish,
  };

  // Score all recipes
  const scoredRecipes = allRecipes
    .map(recipe => {
      let score = scoreRecipe(recipe, slot, context);

      // Boost recipes that fulfill unmet dietary requirements
      if (dietaryContext.needsVegetarian && isVegetarianRecipe(recipe)) {
        score += 50;
      }
      if (dietaryContext.needsFish && isFishRecipe(recipe)) {
        score += 50;
      }

      return {
        recipe,
        score,
        badges: generateBadges(recipe, metadata, dietaryContext),
      };
    })
    .filter(r => r.score > -500) // Filter out disqualified recipes
    .sort((a, b) => b.score - a.score);

  // Return top N suggestions, re-ranked for intra-list diversity so the user
  // doesn't see e.g. three salmon dishes in a row.
  const topSuggestions = selectDiverseSuggestions(scoredRecipes, count);

  console.log(`getTopSuggestions - Returning ${topSuggestions.length} suggestions for ${slot.dayName} ${slot.mealType}`);
  if (topSuggestions.length > 0) {
    console.log('  Top suggestion:', topSuggestions[0].recipe.title, 'score:', topSuggestions[0].score);
    return { suggestions: topSuggestions, usedFallback: false };
  }

  // Fallback: the strict pass returned nothing (e.g. very small library, every
  // candidate already used this/last week, or all filtered out by scoring).
  // Re-score against an empty context so dedup penalties drop, and skip the
  // -500 disqualification floor. The user always gets at least *something*
  // rather than seeing "Suggested for X" with an empty list.
  console.warn(
    `getTopSuggestions - strict pass empty for ${slot.dayName} ${slot.mealType}, applying fallback`
  );
  const fallbackContext: GenerationContext = {
    previousWeekRecipeIds: [],
    usedRecipeIds: new Set<string>(),
    usedProteins: new Map(),
    usedCuisines: new Map(),
    usedDishFormats: new Map(),
    usedKeyFlavors: new Map(),
    metadata,
  };
  const fallbackSorted = allRecipes
    .map(recipe => ({
      recipe,
      score: scoreRecipe(recipe, slot, fallbackContext),
      badges: generateBadges(recipe, metadata, dietaryContext),
    }))
    .filter(r => r.score > -500)
    .sort((a, b) => b.score - a.score);
  const fallbackScored = selectDiverseSuggestions(fallbackSorted, count);

  return { suggestions: fallbackScored, usedFallback: fallbackScored.length > 0 };
}

/**
 * Get day name abbreviation
 */
export function getDayNameShort(dayOfWeek: number): string {
  return DAY_NAMES_SHORT[dayOfWeek];
}

/**
 * Get full day name
 */
export function getDayName(dayOfWeek: number): string {
  return DAY_NAMES[dayOfWeek];
}

export default {
  generateWeekSlots,
  generateWeeklyPlan,
  getSuggestion,
  getTopSuggestions,
  detectProtein,
  detectCuisine,
  isVegetarianRecipe,
  isFishRecipe,
  isSlowCookerRecipe,
  isQuickMeal,
  scoreRecipe,
  getDayNameShort,
  getDayName,
  matchRecipeToDeals,
  findDealOptimizedRecipes,
  calculateDealBonus,
  calculateDeliveryBonus,
  selectVedetteIngredient,
  calculateFreshnessBonus,
  // Dinner-only MVP
  generateDinnerOnlySlots,
  generateDinnerOnlyPlan,
  getDinnerSuggestion,
};
